import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

/* eslint-disable @typescript-eslint/no-floating-promises */
i18n
  // passes i18n down to react-i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    fallbackLng: 'en',
    detection: {
      order: ['localStorage'],
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    },
    resources: {
      en: {
        translation: {
          Sidebar: {
            Main: 'Main',
            Monitor: 'Monitor',
            SecondaryMonitor: 'Admin Monitor',
            Overstock: 'Overstock',
            AskBops: 'Agente CPFR',
            Configuration: 'Configuration',
            Logout: 'Logout',
            Toolkit: 'Toolkit',
            Documentation: 'Documentation',
            DocumentationPage: 'Page {{number}}',
            Insights: 'Insights',
            TrainingVideos: 'Training Videos',
            Certification: 'Certification',
            ResetDashboard: 'Reset to original dashboard',
            Credentials: 'Credentials',
            Chains: 'Retailers',
            Tools: 'Tools',
            Gpc: 'GPC Browser',
            Syncfonia: 'Syncfonia+',
            Verified: 'Verified',
            Users: 'Users',
            Suppliers: 'Suppliers',
            CredentialsSummary: 'Credentials Summary',
            Collapse: 'Collapse',
            ManualUpload: 'Manual Upload',
            Master: 'Master Files',
            Policies: 'Policies',
            Policy: 'CPFR',
            CPFR: 'CPFR',
            Integrations: 'Integrations',
            Store: 'Retail',
            Schedules: 'Schedules',
            FieldServices: 'Field Services',
          },
          Changelog: {
            ChangeLog: 'ChangeLog',
            PopUpTitle: "See what's new in Bops!",
            EmptyEntries: 'No updates available yet. Stay tuned!',
            EmptyDetails: 'No details available.',
          },
          Documentation: {
            LoadMoreButton: 'Load More...',
            EmptyDocumentationPage: 'No documentation available for this page.',
            EmptyDataBase: 'No data available in this database.',
          },
          Guides: {
            Wrl: {
              badge: 'Integration guide',
              title: 'Connect Bops with Walmart Retail Link',
              subtitle:
                'Bops can connect and extract data from Walmart Retail Link through a <bold>dedicated bot account</bold>. This guide covers the initial account setup and the periodic update of its credentials.',
              Prereqs: {
                title: 'Requirements',
                description: 'Before starting, define the two people and accounts involved in the process.',
                Admin: {
                  title: 'WRL Administrator',
                  description:
                    'Responsible for creating the dedicated bot account for Bops within Walmart Retail Link.',
                },
                Bot: {
                  title: 'WRL Bot Account',
                  descriptionPre: 'Choose the email to use for the bot, e.g. ',
                  descriptionPost: '. This user is responsible for:',
                  duty1: 'Create the initial password for the account.',
                  duty2:
                    'Manage the password and token in Bops, since Walmart requires both to expire approximately every ~60 days.',
                },
              },
              Configure: {
                title: 'Set up the bot account',
                description: 'The step-by-step process to create a new bot account in Walmart Retail Link.',
                s1: {
                  title: 'Access Walmart Retail Link',
                  body: 'The WRL Administrator must log in to Walmart Retail Link.',
                  imgAlt: 'Walmart Retail Link login screen',
                  imgCaption: 'Walmart Retail Link login screen.',
                },
                s2: {
                  title: 'Open user management',
                  b1: 'Click on the initials or profile picture in the top-right corner.',
                  b2: 'Select <bold>User Management</bold>.',
                  imgAlt: 'Profile menu with the User Management option',
                  imgCaption: 'The profile menu opens User Management.',
                },
                s3: {
                  title: 'Add a new user',
                  body: 'Click <bold>Add User</bold> in the top-right corner.',
                  imgAddAlt: 'Add User button in User Management',
                  imgAddCaption: 'The Add User button is in the top-right.',
                  formIntro: 'Complete the required fields for the new user:',
                  fields: {
                    name: {
                      label: 'First / Last Name',
                      value: 'First and last name of the bot account owner.',
                    },
                    email: {
                      label: 'Email',
                      value: 'The email selected in the preliminary steps.',
                    },
                    phone: {
                      label: 'Phone number',
                      value: "The account owner's phone number.",
                    },
                    botAccount: {
                      label: 'Bot account',
                      value: 'Check the <bold>This is a bot user account</bold> box.',
                    },
                    employeeType: { label: 'Employee type', value: 'Direct Employee.' },
                    position: {
                      label: 'Position',
                      value: 'The description that best fits the role.',
                    },
                    role: { label: 'Role', value: 'Full Access Additional User.' },
                    supplier: {
                      label: 'Supplier',
                      value: 'The corresponding supplier from the dropdown menu.',
                    },
                  },
                  submit: 'Finally, click <bold>Add New User</bold> to complete the account creation.',
                  imgFormAlt: 'User creation form with the bot checkbox marked',
                  imgFormCaption: 'Completed form with the This is a Bot User Account box checked.',
                  imgSuccessAlt: 'User added confirmation message',
                  imgSuccessCaption: 'Confirmation: the user was added successfully.',
                },
                s4: {
                  title: 'Review the account in the active users list',
                  b1: 'Go to the <bold>Active Account List</bold>.',
                  b2: 'Locate the new bot account.',
                  b3: 'Click the three-dot icon (…) next to the account.',
                  b4: 'Select <bold>View Profile</bold>.',
                  imgAlt: 'Active users list with View Profile menu',
                  imgCaption: 'The three-dot menu opens View Profile.',
                },
                s5: {
                  title: 'Generate and copy the token',
                  body: 'Inside the account profile, generate and copy the <bold>bot token</bold>.',
                  imgAlt: 'Bot profile with Generate Bot Token button',
                  imgCaption: 'Use Generate Bot Token and copy the token with the copy icon.',
                },
              },
              Register: {
                title: 'Set up the password and register in Bops',
                tocLabel: 'Password and registration in Bops',
                description: 'The bot account owner completes the connection by registering credentials in Bops.',
                step1:
                  'The owner will receive an email from Walmart Retail Link confirming the account was created and indicating they must set a password.',
                step2:
                  'Once the password is set, go to the <bold>Credentials</bold> section of the Bops platform and register the WRL credentials.',
                step3Intro: 'In that section, the owner must:',
                check1: 'Accept the Terms and Conditions.',
                check2: 'Enter their personal information.',
                check3: 'Register the username, password, and token for their WRL account.',
                noteTitle: 'Important note',
                note: 'The password and token must be updated approximately <bold>every 60 days</bold> to keep data extraction running without interruptions.',
              },
              Update: {
                title: 'Update existing credentials',
                tocLabel: 'Update credentials',
                description:
                  'When the bot account password or token expire, update <bold>both credentials</bold> to ensure uninterrupted access.',
                s1: {
                  title: 'Access the WRL administrator account',
                  body: 'Log in to Walmart Retail Link with the administrator account.',
                },
                s2: {
                  title: 'Open user management',
                  body: 'From the profile menu, select <bold>User Management</bold>.',
                },
                s3: {
                  title: 'Select the bot account user',
                  body: 'Use the search bar to find the WRL bot account used for periodic data extraction.',
                },
                s4: {
                  title: 'Update the token',
                  body: 'In the account profile, click <bold>Generate Token</bold> to update the credential, then copy it with the copy icon.',
                  imgAlt: 'Generate and copy the bot token',
                  imgCaption: 'Generate Bot Token regenerates the token; copy it with the icon.',
                },
                s5: {
                  title: "Change the bot account's password",
                  b1: 'Go back to the login page and click <bold>Forgot Password</bold>.',
                  b2: 'Enter the current and new password for the bot account, and confirm your selection.',
                  b3: 'You will receive a validation email at the bot account address.',
                  b4: 'Click the link in the email to confirm the password change.',
                },
                s6: {
                  title: 'Share the new credentials with Bops',
                  body: 'With the updated token and password, go to the <bold>Credentials</bold> section of the Bops platform and update both credentials to resume data extraction.',
                  imgAlt: 'Credentials section of the Bops platform',
                  imgCaption:
                    'Credentials section of the Bops platform, with the Walmart Retail Link connection active.',
                },
              },
            },
            Footer: {
              label: 'Integration guide',
              support: 'Questions? Email us at',
              tocAriaLabel: 'Table of contents',
              tocLabel: 'In this guide',
            },
          },
          Global: {
            ErrorMessage:
              'Something has gone wrong.\nOur team is working to fix it. If you need help, reach out to your account operator or contact us at support@bops.ai.',
            WaitingMessage: 'Please wait...\nbops is getting your data',
            UnathorizedMessage:
              "You're not authorized to view this page.\nIf you think this is an error please contact us at support@bops.ai.",
            PageNotFoundMessage:
              "Page not found.\nWe can't seem to find the page you're looking for.\nClick below to go to the main monitor.",
            NoAccountMessage:
              'It seems like your access is not yet configured!\n\nPlease contact your account administrator to complete the setup\nand gain access to your inventory recommendations.',
            GoHome: 'Main monitor',
            GoBack: 'Go back',
            TryAgain: 'Try again',
            Close: 'Close',
            WorkInProgressTitle: 'Almost ready',
            WorkInProgressMessage:
              'We’re putting the finishing touches on this section. We’ll let you know the moment it’s available.',
            Confirm: 'Confirm',
            Canceled: 'Cancel',
            Select: 'Select',
            Required: 'is required',
            Page: 'Page',
            Of: 'of',
            Next: 'Next',
            Previous: 'Previous',
            WelcomeMessage: 'Welcome to your',
            WelcomeMessage2: 'Business Operations Platform',
          },
          Recommendation: {
            Loading: "Hang on we're gathering your recommendations...",
            ImportantOverstock: 'Important Overstock',
            Overstock: 'Overstock',
            PotentialOverstock: 'Potential Overstock',
            PotentialUnderstock: 'Potential Understock',
            Understock: 'Understock',
            PotentialStockout: 'Potential Stockout',
            StoreStockout: 'Store Stockout',
            CompleteStockout: 'Complete Stockout',
            InventoryEfficiencyScore: 'Inventory Efficiency Score',
            InventoryDays: 'Inventory Days',
            Dashboard: 'Dashboard',
            WelcomeMessageHi: 'Hi',
            WelcomeMessageHere: 'Here',
            WelcomeMessageFinal:
              ' is a summary of your current score and {{length}} recommendations that can help simplify your inventory decision-making process.',
            TopRecommendations: 'Top {{length}} Recommendations',
          },
          Monitor: {
            Loading: "Hang on we're gathering your dashboard...",
            DashboardNotFound:
              "The dashboard you're looking for doesn't exist.\nPlease select one from the sidebar or click below to return to the main monitor.",
            // Copy stays in Spanish regardless of active locale — Logyca
            // product text for a Spanish-speaking client (Ticket 6).
            WaitingScreen: {
              Generic: {
                Title: 'Estamos configurando tu plataforma',
                Subtitle: 'Estamos preparando tu espacio. En breve tendrás acceso a tus dashboards.',
              },
              AvanzadoDashboardPending: {
                Title: 'Estamos configurando tu plataforma',
                Subtitle: 'Estamos preparando tu espacio. En breve tendrás acceso a tus dashboards.',
              },
              PlanChangeValidation: {
                Title: 'Estamos validando tu cambio de plan',
                Subtitle: 'En breve confirmaremos tu nuevo plan y tendrás acceso a tus dashboards actualizados.',
              },
              ScopeExpansionReview: {
                Title: 'Estamos validando tu cambio de plan',
                Subtitle: 'Nuestro equipo está confirmando los detalles de tu cuenta antes de habilitar el nuevo plan.',
              },
            },
          },
          Configuration: {
            Loading: "Hang on we're gathering your configuration files...",
            Title: 'Master Files Configuration',
            Message:
              'These files are used to drive the configuration of your inventory recommended actions.\nConfiguration updates will be applied by the end of the business day.',
            Open: 'Open',
            Download: 'Download',
            DownloadError: 'Error downloading file',
            Uploading: 'Uploading',
            Upload: 'Upload',
            Cancel: 'Cancel',
            Replace: 'Replace',
            Error: 'There has been an error updating the file, please try again.',
            Success: 'File uploaded successfully!',
            All: 'All configurations',
            LoadingLink: "Hang on we're gathering your configurations ...",
            Name: 'Name',
            Status: 'Status',
            Tags: 'Tags',
            Active: 'Active',
            InProgress: 'Updating',
            Processing: 'Processing',
            Configuring: 'Configuring',
            Failed: 'Error during upload',
            NewMaster: 'New Master',
            LastUpdated: 'Last updated',
            AddMaster: 'Add new master',
            ModalTitle: 'New Master File',
            Connector: 'Source',
            MasterType: 'Master File Name',
            SelectConnector: 'Select a source',
            SelectMasterType: 'Enter master file name',
            LoadingModal: 'Generating file, this might take a few minutes. Do not close this window.',
            SuccessModal: 'A new master file has been generated successfully!',
            ChooseFile: 'Choose file',
            SelectConnectorError: 'Please select a source',
            SelectFileError: 'Please select a file to upload',
            SelectMasterTypeError: 'Please enter a master file name',
            NewMasterError: 'There has been an error uploading the new master file, please try again.',
            ResourceLockedError: 'Cannot update master file because another user is editing it.',
            Continue: 'Continue',
            Back: 'Back',
            GenericSource: 'Generic',
            SourceHelper:
              'Which source the data belongs to. Pick Generic if it does not belong to one, or applies to several.',
            ConfirmTitle: 'Check before creating',
            ConfirmMessage: 'This is how the master file will be created. Go back if something is off.',
            ConfirmName: 'Name',
            ConfirmTag: 'Tag',
            ConfirmFile: 'File',
            ConfirmCreate: 'Create master file',
            PreviewTitle: 'What this file would change',
            PreviewMessage: 'You are replacing the contents of {{master}} with {{file}}.',
            PreviewRows: 'Rows',
            PreviewColumns: 'Columns',
            PreviewColumnsWarning: 'This change includes a schema change.',
            PreviewNoChanges: 'Nothing changes between the two files. Check you picked the right one.',
            ConfirmReplace: 'Replace file',
          },
          Credentials: {
            Loading: "Hang on we're gathering your credentials...",
            Title: 'Credentials',
            Subtitle: 'Update your data provider credentials here',
            Username: 'Username',
            Password: 'Password',
            Token: 'Token',
            Account: 'Account',
            SasUrl: 'SAS Access URL',
            Update: 'Update',
            Accept: 'Accept',
            Save: 'Save',
            Cancel: 'Cancel',
            TitleModal: 'Update Credentials',
            Name: 'Name',
            Added: 'Added on',
            LastUpdated: 'Last updated',
            AllCredentials: 'All Credentials',
            Activate: 'Activate',
            Pending: 'Pending',
            ExpiringSoon: 'credential(s) expiring soon',
            ExpiringSoonTooltip: 'These credential will be expiring soon.',
            LoadingModal: 'Checking if  the {{name}} configuration is okay',
            UpdateSuccess: 'The credentials have been successfully updated',
            Status: 'Status',
            Error: "There's been an error updating the credentials, please try again.",
            RequestCredentials:
              "It seems like we don't have your inventory data!\n\nPlease update your credentials to complete the setup\nand gain access to your inventory recommendations.",
            RequestCredentialsButton: 'Set Credentials',
            HasCredentialsButton: 'Documentation',
            Success: 'Credentials have been successfully updated',
            Active: 'Active',
            Paused: 'Paused',
            Expired: 'Expired',
            Processing: 'Processing',
            ExpiredCount: 'credential(s) expired',
            ExpiredAndExpiring: '{{expired}} credential(s) expired, {{expiring}} expiring soon',
            ExpiredTooltip: 'These credential have expired.',
            ExpiredPopupPrefix: '{{count}} credential(s) expired. This might lead to the data being out of date',
            UpdateMessage: 'Please update it:',
            Here: 'Here',
            ExpiringPopupPrefix: '{{count}} credential(s) expiring soon',
            HasCredentials:
              'Thank you for joining Inventory Intelligence!\n\nWe are processing your product information for analysis. If you have any questions, please contact the support team present in the documentation.',
            ErrorInvalidCredentials: 'Credentials are invalid or expired. Verify them and try again.',
            StatusReason: {
              RpaBotBlocked:
                'These credentials are not valid for automated access — check your account status with your administrator. For questions, see the credentials manual: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
              UserBlocked:
                'This user is blocked — check your account status with your administrator. For questions, see the credentials manual: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
              InvalidUser:
                'The username entered is not valid — check your credentials and try again. For questions, see the credentials manual: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
              InvalidPassword:
                'The password entered is not valid — try again. For questions, see the credentials manual: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
              PasswordExpired:
                'The password entered has expired — update the password and token directly from the WRL platform. For questions, see the credentials manual: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
              PasswordChangeRequired:
                'A password change is required — update the password and token directly from the WRL platform. For questions, see the credentials manual: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
              CredentialsExpired:
                'These credentials have expired — update the password and token directly from the WRL platform. For questions, see the credentials manual: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
            },
            InvalidUrlScheme: 'Invalid SAS URL: the URL must start with https://.',
            InvalidUrlHost: 'Invalid SAS URL: the URL must point to storageproveedores.blob.core.windows.net.',
            InvalidUrlSignature: 'Invalid SAS URL: missing the sig= access signature. Copy the full URL from Soriana.',
            InvalidUrlMissingId:
              'Invalid SAS URL: missing the account id (e.g. /portalproveedores/{id}). Copy the full URL from Soriana.',
            SamePassword: 'Password unchanged. Update it before saving here.', // pragma: allowlist secret
            SameToken: 'Token unchanged — generate a new one before saving here.',
            NoChangesDetected: 'You cannot update the credentials until the following field(s) are updated:',
            AddCredential: 'Add Credential',
            AddCredentialTitle: 'Add New Credential',
            SelectConnector: 'Select a connector',
            CreateSuccess: 'The credential has been successfully created',
            CredentialAlreadyExists: 'A credential already exists for this connector',
            EmptyTitle: 'No chains connected yet',
            EmptyDescription: 'You don’t have any connectors available for your account yet.',
            SectionActive: 'Active',
            SectionAvailable: 'Available to connect',
            SectionComingSoon: 'Coming soon',
            Card: {
              StatusConnected: 'Connected',
              StatusAttention: 'Attention',
              StatusExpiring: 'Expiring soon',
              StatusExpired: 'Expired',
              StatusAvailable: 'Available',
              LastSync: 'Last update',
              User: 'User',
              Edit: 'Edit',
              AvailableHint: 'Not connected yet · available to use',
              Connect: 'Connect',
              Help: 'Help',
              WarningExpired: 'Credentials expired. Update them to resume syncing.',
              WarningExpiring: 'Credentials expiring soon. Update your credentials.',
            },
            CountLabel_one: '{{count}} credential',
            CountLabel_other: '{{count}} credentials',
            EmptySearch: 'No credentials match "{{term}}"',
            SortDefault: 'Default order',
            SortByStatus: 'Sort by status',
            SortByName: 'Sort by name',
            ViewCards: 'Cards',
            ViewList: 'List',
            Search: 'Search credentials',
            List: {
              Retailer: 'Retailer',
              Status: 'Status',
              User: 'User',
              Updated: 'Updated',
              Actions: 'Actions',
            },
            ComingSoon: {
              Badge: 'Coming soon',
              Notify: 'Notify me',
            },
            Placeholder: {
              Username: 'Username',
              Email: 'Email',
              Password: 'Password',
              Token: 'Access token',
              Account: 'Account',
              SasUrl: 'https://storageproveedores.blob.core.windows.net/...?sv=...&sig=...',
            },
            Form: {
              ConnectTitle: 'Connect {{name}}',
              UpdateTitle: 'Update {{name}} credentials',
              Cancel: 'Cancel',
              Connect: 'Connect',
              Update: 'Update',
              ShowPassword: 'Show password',
              HidePassword: 'Hide password',
              Description: {
                Default: 'Enter your credentials to connect {{name}}.',
                Walmart: 'Enter your Retail Link credentials to connect Walmart.',
                Soriana: 'Enter the SAS access URL provided by Soriana to connect your account.',
              },
            },
            Instructions: {
              NoteLabel: 'Note',
              GuideLabel: 'Integration guide',
              OpenGuide: 'View setup guide',
              Soriana: {
                Title: 'How to get your Soriana SAS URL',
                Steps: [
                  'Sign in to the Soriana Vendor Portal at https://proveedores.soriana.com',
                  'Go to Settings > Integrations > Data Access.',
                  'Locate the SAS URL for your supplier account.',
                  'Copy the complete URL (it includes your access signature). It should look like: https://storageproveedores.blob.core.windows.net/portalproveedores/...?sv=...&sig=...',
                  'Paste the URL here in Bops to connect your account.',
                ],
                Note: 'The SAS URL contains a time-limited access signature. When it expires, return to the Soriana portal to generate a new one and update it here.',
              },
            },
          },
          CredentialIntake: {
            Title: 'Share your credentials',
            Description: 'Select a connector below and enter your credentials to get started.',
            Loading: 'Loading...',
            InvalidLink: 'This link is invalid or has expired. Please contact your BOPS representative for a new one.',
            Submitted: 'Submitted',
          },
          ManualUpload: {
            Title: 'Manual Upload',
            Uploading: 'Uploading',
            Loading: "Hang on we're gathering the files you can upload...",
            Upload: 'Upload',
            LoadingProgress: 'Uploading files',
            All: 'All files that can be uploaded',
            Success: 'have been uploaded successfully!',
            Error: 'There has been an error uploading the file, please try again.',
          },
          User: {
            PasswordLengthError: 'Password must be at least 12 characters long', // pragma: allowlist secret
            PasswordUppercaseError: 'Password must contain at least one uppercase letter', // pragma: allowlist secret
            PasswordLowercaseError: 'Password must contain at least one lowercase letter', // pragma: allowlist secret
            PasswordNumberError: 'Password must contain at least one number', // pragma: allowlist secret
            Password: 'Password',
            Title: 'Users',
            ErrorEmail: 'Introduce a valid email',
            UsernameNotEmail: 'The username cannot be the same as the email',
            ErrorUsername:
              'Username can only contain alphanumeric characters and the following characters: "_" ,"+", "-", ".","!","#", "$", "^","~", "@", ","',
            BlockLabel: 'Block',
            UnBlockLabel: 'Unblock',
            ChangeAccount: 'Change client',
            ErrorCreate: 'Error creating user',
            Edit: 'Edit',
            ErrorAlreadyExists: 'Error creating user. User already exists',
            EditSuccess: 'User has been edited successfully',
            ErrorBlock: 'Error blocking user',
            Loading: "Hang on we're gathering your users...",
            ConfirmBlock: 'Are you sure you want to block the user\n',
            EmailSent: 'Email sent to user\n',
            ConfirmUnBlock: 'Are you sure you want to unblock the user\n',
            ConfirmChangePassword: 'Are you sure you want to send an email to change the password to\n', // pragma: allowlist secret
            ChangePassword: 'Send change password email', // pragma: allowlist secret
            ErrorChangePassword: 'Error changing password', // pragma: allowlist secret
            LoadingChangePassword: 'Sending email to change password to user \n', // pragma: allowlist secret
            Select: 'Select a client',
            Add: 'Add new user',
            Never: 'Never',
            All: 'All Users',
            TitleModal: 'New User',
            TitleEditModal: 'Edit User',
            ErrorChangeAccount: 'Error changing client',
            AddSuccess: 'has been added successfully!',
            LoadingCreate: 'User is creating',
            LoadingEdit: 'User is editing',
            Client: 'Client',
            Tags: 'Tags',
            EditTags: 'Edit tags',
            EditTagsPlaceholder: 'Select tags for the user',
            EditTagsEmpty: 'No tags available',
            EditTagsLoadingError: 'Error loading available tags',
            EditTagsError: 'Error updating user tags',
            EditPermissions: 'Edit permissions',
            EditPermissionsPlaceholder: 'Select permissions for the user',
            EditPermissionsEmpty: 'No permissions available',
            EditPermissionsLoadingError: 'Error loading available permissions',
            EditPermissionsError: 'Error updating user permissions',
            ErrorSameClient: 'You cannot change the user to the same client',
            Status: 'Status',
            Email: 'Email',
            Username: 'Username',
            LastLogin: 'Last Login',
            Search: 'Search by email',
            Blocked: 'Blocked',
            Active: 'Active',
            UsernameLengthError: 'Username must be between 6 and 30 characters',
            DeleteConfirm: 'Are you sure you want to delete the user\n',
            ErrorDelete: 'Error deleting user',
            Delete: 'Delete',
            LoadingDelete: 'Deleting user\n',
            ErrorInvalidUsername: 'Invalid username',
            ErrorUsernameTaken: 'That username is already taken. Please choose a different one',
            UsernameCannotBeEmail: 'Username cannot be an email',
            UserModalLogoutMessage: 'Are you sure you want to Logout?',
          },
          Supplier: {
            Loading: "Hang on we're gathering your suppliers...",
            Add: 'Add supplier',
            Title: 'Suppliers Management',
            Progress: 'In Progress',
            WithData: 'Data available',
            WithoutData: 'Missing Data',
            WithDataTooltip: 'Data for this supplier is available',
            WithoutDataTooltip:
              'Data for this supplier is not available. Please request credentials for Walmart/Soriana.',
            ProgressTooltip: 'Creation is in progress. Supplier data will be available within 24 hours.',
            Exist: 'Supplier already exists',
            AddMessage: 'has been successfully added',
            Status: 'Status',
            Name: 'Name',
            Plan: 'Plan',
            Connectors: 'Credentials',
            CreatedAt: 'Creation date',
            Export: 'Export list',
            ExportFilename: 'suppliers',
            Error: 'Error creating the supplier',
            LoadingCreate: 'Creating supplier',
            All: 'All Suppliers',
            TitleModal: 'New Supplier',
            Search: 'Search for GLN or name',
            Pending: 'Pending for refresh data',
            UserEmail: 'User email',
            SelectPlan: 'Select a plan',
            PlanRequired: 'Plan is required',
            PlanRequiresManualSetup: 'Requires manual setup — contact your account manager.',
            SiebelLookupError: "We couldn't fetch this GLN's data — please retry, or fill in Name and Plan manually.",
            SiebelLookupPending: 'Looking up GLN data...',
            SiebelPrefillOverwrittenNameAndPlan: 'GLN changed — Name and Plan were refreshed from Siebel data.',
            SiebelPrefillOverwrittenNameOnly:
              "GLN changed — Name was refreshed from Siebel data. Plan couldn't be resolved; please select it manually.",
            AddonsColumn: 'Add-ons',
            AddonsCount: '{{count}} add-ons',
            ManageAddons: 'Manage add-ons',
            UpdatePlan: 'Update plan',
            ChangePlanTitle: 'Update plan',
            ChangePlanWarning: 'Updating the plan will remove the {{count}} active add-ons.',
            PlanChangeError: 'Error updating the plan',
            PlanChangeSuccess: 'Plan updated successfully',
            PlanChangePending:
              'Your request was received, but this change requires manual review before it takes effect.',
            AddonsModalTitle: 'Manage add-ons',
            AddonsUpdateError: 'Error updating add-ons',
            AddonsUpdateSuccess: 'Add-ons updated successfully',
            NoAddonsAvailable: 'No add-ons available for this plan',
            AddonsActive: 'Active',
            AddonsAvailable: 'Available',
            AddonsSelectedCount: '{{count}} add-ons selected',
            RowActions: 'Supplier actions',
            GenerateCredentialsLink: 'Generate credentials link',
            CredentialLinkCopied: 'Link copied to clipboard',
            CredentialLinkError: 'Error generating the link',
            Save: 'Save',
            SaveChanges: 'Save changes',
          },
          CredentialsSummary: {
            Title: 'Credentials Summary',
            Loading: "Hang on we're gathering the credentials summary...",
            All: 'All Credentials',
            Search: 'Search GLN or name',
            FilterByConnector: 'Filter by connector',
            FilterByStatus: 'Filter by status',
            Gln: 'GLN',
            ProviderName: 'Provider',
            Connector: 'Connector',
            Status: 'Status',
            DaysUntilExpiration: 'Days until expiration',
            LastUpdated: 'Last updated',
            CreatedAt: 'Creation date',
          },
          Mfa: {
            Title: 'Multifactor Authentication',
            Loading: "Hang on we're gathering your phones...",
            Phone: 'Phone',
            Message: 'Registered Phones',
            Code: 'Get Code',
            ErrorCode: 'There was an error getting the code',
            Close: 'Close',
            TitleModal: 'Code',
            Description: 'Description',
          },
          Integration: {
            All: 'All Integrations',
            Loading: "Hang on we're gathering your integrations...",
            Title: 'Integrations',
            Client: 'Client',
            Status: 'Status',
            LastUpdated: 'Last updated',
            File: 'File Name',
            Late: 'Late',
            OnTime: 'On Time',
            NextCron: 'Next Date',
            Path: 'Path',
            Ignore: 'Ignore',
            FileType: 'File Type',
            Connector: 'Connector',
            SearchByPath: 'Search by path',
            SortNotAvailable: 'Sorting is currently unavailable for this column.',
            SelectClient: 'Select client(s)',
            SelectFileType: 'Select file type(s)',
            SelectConnector: 'Select connector(s)',
          },
          MasterSourceSelect: {
            Column: 'Column',
            NotSpecified: 'Not specified',
            SelectMasterSource: 'Select Master Source',
            ColumnName: 'Column Name',
            EnterColumnName: 'Enter column name',
            ColumnNameDescription: 'Specify the column name in the master data that contains the values',
            Edit: 'Edit',
            Cancel: 'Cancel',
            SaveChanges: 'Save Changes',
          },
          Agent: {
            // UI Messages
            InputPlaceholder: 'Ask, search, or make anything...',
            InputPlaceholderShort: 'Ask a question...',
            OptionsHintWriteCustom: 'or type something else',
            SendButton: 'Send',
            StopButton: 'Stop generation',
            AttachTooltip: 'Attach file',
            RemoveAttachment: 'Remove attachment',
            CopyResponse: 'Copy response',
            Copied: 'Copied',
            SaveTitle: 'Save',
            CancelSaveTitle: 'Cancel',

            // Header Controls
            NewChat: 'New chat',
            SidebarMode: 'Sidebar mode',
            FloatingMode: 'Floating mode',
            Minimize: 'Minimize',
            Maximize: 'Maximize',
            CloseChat: 'Close chat',
            HideSidebar: 'Hide sidebar',

            // Thinking Indicator
            HideSteps: 'Hide steps',
            ShowSteps: 'Show {{count}} process steps',

            // Date Formatters
            Today: 'Today',
            Yesterday: 'Yesterday',
            DaysAgo: '{{days}} days ago',
            Global: 'Global',

            // Sidebar indicators
            Processing: 'Processing',
            NewResponse: 'New response',

            // Welcome Section
            WelcomeTitle: 'OSA Assistant',
            WelcomeDescription:
              'I analyze metrics, trends and help optimize inventory based on data from the active module.',
            LoadingMessages: 'Loading messages...',
            GuidedJourneys: 'Guided Journeys',
            LoadingJourneys: 'Loading guided journeys...',
            LoadingAnalysis: 'Loading your analysis...',
            WelcomeGreeting: 'Hello, {{name}}!',

            // Onboarding UI
            OnboardingPressEnter: 'Press Enter to continue',
            OnboardingContinueWith: 'Continue with {{count}} selected',
            OnboardingCompletionTitle: 'Perfect, {{name}}.',
            OnboardingCompletionSubtitle: 'All set. Your experience is configured.',
            OnboardingCompletionMessage: 'Welcome to your intelligent OSA assistant.',
            OnboardingSkipHint: 'Press Esc to skip',
            OnboardingSkipHintMobile: 'Tap to skip',
            OnboardingSaveError: 'Error saving. Please try again.',
            PreferencesBannerHighlight: 'Personalize your experience',
            PreferencesBannerText: 'with the assistant',
            PersonalizeSubtitle: 'Tell me a bit about you.',
            GenericBrand: 'OSA Assistant',
            GreetingPersonal: 'Hello, {{name}}.',
            GreetingPrompt: 'Where would you like to start today?',
            GreetingSubtitle:
              'I analyze metrics, trends and help optimize inventory based on data from the active module.',
            GuidedJourneysLabel: 'Guided journeys',
            ComposerPlaceholder: 'Ask, search or create what you need...',
            ComposerDisclaimer: 'OSA Agent can make mistakes. Confirm important information.',
            ConnectorListTitle: 'Switch retailer',
            ConversationsListTitle: 'Conversations',
            SearchPlaceholder: 'Search conversations...',
            GroupToday: 'Today',
            GroupYesterday: 'Yesterday',
            GroupThisWeek: 'This week',
            GroupOlder: 'Older',
            EmptyConversationsTitle: 'No conversations yet',
            EmptyConversationsDescription: 'Start a new chat to see it here.',
            EmptySearchTitle: 'No results',
            EmptySearchDescription: 'Nothing matches "{{term}}".',
            EmptyJourneysTitle: 'No guided journeys',
            EmptyJourneysDescription: 'Insights for this connector will appear here when they’re ready.',
            EmptyTitle: 'Untitled',
            RenameAction: 'Rename',
            DeleteAction: 'Delete',
            DeleteTitle: 'Delete conversation',
            DeleteDescription:
              'This action cannot be undone. Conversation "{{title}}" and all its interactions will be deleted.',
            Cancel: 'Cancel',
            ThinkingSummary_one: 'Thought for {{seconds}}s · {{count}} step',
            ThinkingSummary_other: 'Thought for {{seconds}}s · {{count}} steps',
            ThinkingStepsCount_one: '{{count}} step',
            ThinkingStepsCount_other: '{{count}} steps',
            AriaResizeSidebar: 'Resize agent sidebar',
            AriaClearSearch: 'Clear search',
            AriaDisplayMode: 'Agent display mode',
            AriaOpenAgent: 'Open OSA Agent',
            AriaConversationActions: 'Conversation actions',
            AriaVoiceover: 'Voiceover',
            AriaShowThinkingLog: 'Show thinking log',
            AriaHideThinkingLog: 'Hide thinking log',
            AriaScrollToBottom: 'Scroll to latest message',
            AriaRegenerate: 'Regenerate',
            PreferencesModalTitle: 'Preferences',
            PreferencesModalSubtitle: 'Edit how I interact with you',
            PreferencesSave: 'Save changes',
            PreferencesClearAll: 'Clear all preferences',
            PreferencesClearConfirm: 'Are you sure? This will reset your personalization.',
            PreferencesClearConfirmAction: 'Delete',
            PreferencesClearCancel: 'Cancel',
            PreferencesSaving: 'Saving...',
            PreferencesDeleting: 'Deleting...',
            EditPreferences: 'Edit preferences',

            // Error Messages (UI level)
            ErrorUnexpected: 'An unexpected error occurred.',
            ErrorTimeout: 'Request took too long. Please try again.',
            ErrorNetwork: 'Connection error. Check your internet connection.',
            ErrorJourneyUnavailable:
              "This guided journey's instructions could not be loaded, so nothing was sent to the agent and no report was generated. Please try again in a moment.",
            ErrorAgentPrefix: 'Error contacting agent:',
            ErrorNoResponse: 'No response received from agent.',
            ErrorAgentUnavailable: 'Sorry, the agent is not available right now.',
            ErrorGenerationStopped: 'Generation stopped by user.',
            FileDownloadError: 'Failed to download file. Please try again.',
            ImageExpand: 'Expand image',
            ImageDownload: 'Download image',
            ImageZoomIn: 'Zoom in',
            ImageZoomOut: 'Zoom out',
            ImageZoomReset: 'Reset zoom',
            ImageClose: 'Close',

            // Thinking Messages
            ThinkingInitial: 'Starting analysis…',

            // Suggestions
            SuggestionTopOpportunitiesDisplay: 'Forecast Accuracy by Category',
            SuggestionTopOpportunitiesPrompt:
              'Where are my demand forecasts failing, and how can I improve forecast accuracy by category?',
            SuggestionBiggestRisksDisplay: 'Products at Risk of Stockout',
            SuggestionBiggestRisksPrompt:
              'Which products are at the highest risk of running out, and what is the impact on lost sales?',
            SuggestionSeasonalAdjustDisplay: 'Reduce Excess Inventory',
            SuggestionSeasonalAdjustPrompt:
              'Which products have excess inventory, and what strategies can I apply to reduce it?',
            SuggestionPerformanceSummaryDisplay: 'Improve On-Shelf Availability',
            SuggestionPerformanceSummaryPrompt: 'Why is my on-shelf availability low, and how can I improve it?',

            // Browser Notifications
            NotificationBannerText: 'This may take a moment. Get notified when the response is ready.',
            NotificationEnable: 'Enable',
            NotificationDismiss: 'Dismiss',
            NotificationTitle: 'OSA Agent',
            NotificationBody: 'Your analysis is ready. Click to view the results.',
            NotificationWaiting: 'Allow notifications in the browser prompt above.',
            NotificationEnabled: 'Notifications enabled. We will notify you if you leave this tab.',
            NotificationDenied: 'Notifications blocked. Please enable them in your browser settings.',

            // Disclaimer
            Disclaimer: 'OSA Agent can make mistakes. Verify important information.',

            // Feedback Modal
            FeedbackTitle: 'Feedback',
            FeedbackSubmit: 'Send',
            FeedbackCancel: 'Cancel',
            FeedbackQuestion: 'Did this answer help?',
            FeedbackHelpful: 'Helpful',
            FeedbackNotHelpful: 'Not helpful',
            FeedbackWhatWentWrong: 'What went wrong? (optional)',
            FeedbackExpectedPlaceholder: 'What did you expect to see? (optional)',
            FeedbackDisclosure: 'The BOPS team reviews this feedback along with the answer. Not used to train models.',
            FeedbackReasonWrongNumbers: 'Wrong numbers',
            FeedbackReasonWrongPeriodOrFilter: 'Wrong period or filter',
            FeedbackReasonMisunderstoodQuestion: 'Misunderstood my question',
            FeedbackReasonMissingDataOrChart: 'Missing data or chart',
            FeedbackReasonTooLongOrConfusing: 'Too long or confusing',
            FeedbackReasonTooSlowOrError: 'Too slow or error',
            FeedbackReasonOther: 'Other',
            FeedbackPositiveTitle: 'Glad it helped',
            FeedbackWhatWorkedWell: 'What worked well? (optional)',
            FeedbackLikedPlaceholder: 'Anything else you want to tell us? (optional)',
            FeedbackReasonAccurateData: 'Accurate data',
            FeedbackReasonClearAnswer: 'Clear answer',
            FeedbackReasonSavedTime: 'Saved me time',
            FeedbackReasonGoodInsight: 'Good insight',
          },
          ConfigurationPolicy: {
            TargetInventory: {
              CalculationType: 'Calculation Type',
              CalculationTypeDesc: 'Select how target inventory values should be calculated',
              BopsOptimalInventory: 'Bops Optimal Inventory',
              BopsOptimalDesc: 'Automated calculation based on service level',
              Provided: 'Provided',
              ProvidedDesc: 'Use values from source data',
              DefaultCalculationType: 'Default Calculation Type',
              Source: 'Source',
              SourceDesc: 'Source of provided target',
              User: 'User',
              UserTooltip: 'Use internal inputs to set inventory targets',
              Customer: 'Customer',
              CustomerTooltip: 'Use customer‑provided values for inventory targets (e.g. retailer)',
              CustomerMessage:
                'Customer provided inventory configuration will be used. No additional settings required.',
              CustomerMessageWalmart:
                'Target Inventory: Maximum number of units that can be held on the shelf on the sales floor. Values will be sourced from Walmart systems.',
              CustomerMessageTarget:
                'Target Inventory: Final Presentation Min Quantity - minimum inventory quantity that must be available in-store to maintain operational availability. Values will be sourced from Target systems.',
              CustomerMessageFarmatodo:
                'Target Inventory: Max Shelf Quantity - maximum allowable inventory quantity for a product at a location to prevent overstocking. Values will be sourced from Farmatodo systems.',
              ConfigurationType: 'Configuration Type',
              ConfigurationTypeDesc: 'Configuration type for provided target',
              Units: 'Units',
              UnitsTooltip: 'Display inventory targets as a count of unit',
              DaysOnHand: 'Days on Hand',
              DaysOnHandTooltip: "Display inventory targets as the number of days' supply",
              UnitsType: 'Units Type',
              UnitsTypeDesc: 'Units type for provided target',
              Target: 'Target',
              TargetDesc: 'Target inventory level',
              TargetTooltip: 'Set target inventory levels',
              ReorderPoint: 'Reorder Point',
              ReorderPointDesc: 'Reorder point inventory level',
              ReorderPointTooltip: 'Set reorder point levels',
              SafetyStock: 'Safety Stock',
              SafetyStockDesc: 'Safety stock inventory level',
              SafetyStockTooltip: 'Set safety stock levels',
              DaysOnHandDirection: 'Days on Hand Direction',
              DaysOnHandDirectionDesc: 'Days on hand direction for provided target',
              Backward: 'Backward',
              BackwardDesc: 'Consider past days of inventory (Sales)',
              Forward: 'Forward',
              ForwardDesc: 'Project forward days of inventory (Forecast)',
              DataSource: 'Data Source',
              DataSourceDesc: 'Data source for provided target',
              DefaultValue: 'Default Value ({{type}})',
              Days: 'Days',
              ServiceLevel: 'Service Level',
              ServiceLevelDesc: 'Specify the target service level (0-100%)',
              ServiceLevelPercent: 'Service Level (%)',
            },
            Demand: {
              DemandIndicator: 'Demand Indicator',
              DemandIndicatorDesc: 'Type of demand data to use for calculations',
              SellOut: 'Sell Out',
              SellOutTooltip: 'Sales directly to end consumers',
              SellIn: 'Sell In',
              SellInTooltip: 'Sales to retailers or distributors',
              Forecast: 'Forecast',
              ForecastTooltip: 'Projected future demand',
              DemandWindow: 'Demand Window',
              DemandWindowDesc: 'Time period to consider for demand calculations',
              FourWeeks: '4 Weeks',
              EightWeeks: '8 Weeks',
              TwelveWeeks: '12 Weeks',
              TwentySixWeeks: '26 Weeks',
              FiftyTwoWeeks: '52 Weeks',
              OneHundredFourWeeks: '104 Weeks',
              OneMonth: '1 month',
              TwoMonths: '2 months',
              ThreeMonths: '3 months',
              SixMonths: '6 months',
              OneYear: '1 year',
              TwoYears: '2 years',
              AdvancedSettings: 'Advanced Settings',
              DemandVariability: 'Demand Variability',
              DemandVariabilityDesc:
                'Additional demand uncertainty factor to account for market fluctuations and forecast accuracy',
              DemandVariabilityPercent: 'Demand Variability (%)',
            },
            Supply: {
              LeadTimes: 'Lead Times',
              LeadTimesDesc: 'Lead times for the supply chain',
              DefaultLeadTimeDays: 'Default Lead Time (Days)',
              AdvancedSettings: 'Advanced Settings',
              LeadTimesVariability: 'Lead Times Variability',
              LeadTimesVariabilityDays: 'Lead Times Variability (Days)',
              ReorderRate: 'Reorder Rate',
              OrderFrequency: 'Order Frequency',
              OrderFrequencyDesc: 'Set frequency parameters for supply ordering patterns and cycles',
              DefaultValue: 'Default {{type}} (Days)',
            },
            Inventory: {
              InventoryOnHand: 'Inventory on Hand',
              InventoryOnHandDesc: 'Include current inventory available in calculations',
              AdditionalInventoryOptions: 'Additional Inventory Options',
              AdditionalInventoryOptionsDesc: 'Include additional inventory options in calculations',
              OnOrder: 'On Order',
              OnOrderTooltip: 'Orders placed with suppliers but not yet received',
              InTransit: 'In Transit',
              InTransitTooltip: 'Inventory in transit between locations',
              InWarehouse: 'In Warehouse',
              InWarehouseTooltip: 'Inventory in distribution centers for replenishing store stock',
              Allocated: 'Allocated',
              AllocatedTooltip: 'Customer orders that have been confirmed but not yet fulfilled',
              Aging: 'Aging',
              AgingTooltip: 'Inventory that can not be sold because its freshness',
              Custom: 'Custom',
              CustomTooltip: 'Custom inventory configuration',
              ImpactIncrease: 'Increases available inventory',
              ImpactDecrease: 'Decreases available inventory',
              ImpactBoth: 'Can increase or decrease available inventory',
            },
            CPFR: {
              Title: 'Smart CPFR Configuration',
              TargetInventory: 'Target Inventory',
              Demand: 'Demand',
              Supply: 'Supply',
              InventoryAvailable: 'Inventory Available',
              UnconfiguredDisclaimerTitle: 'Smart CPFR Not Configured',
              UnconfiguredDisclaimerMessage:
                'Bops recommendations may be inaccurate due to missing configuration. Please contact your Account Operator to complete the Smart CPFR setup.',
            },
            OSA: {
              Title: 'On-Shelf Availability Configuration',
              OsaCalculation: 'OSA Calculation',
              AuditAlerts: 'Audit Alerts',
              BopsRecommended: 'Bops Recommended',
              BopsRecommendedDesc: 'Automated calculation based on historical sales data',
              Provided: 'Provided',
              ProvidedDesc: 'Use values from master files',
              Passive: 'Passive',
              Average: 'Average',
              Aggressive: 'Aggressive',
              AllAlerts: 'All Alerts',
              AllAlertsDesc: 'Select all alerts defined by the OSA calculation',
              AllAlertsInScope: 'All Alerts in Scope',
              TopOpportunityAlerts: 'Top Opportunity (Lost Sales)',
              TopSellerAlerts: 'Top Item Sellers',
              Daily: 'Daily',
              Weekly: 'Weekly',
              Custom: 'Custom',
              MerchandiserIntegration: 'Merchandiser Integration',
              FieldOpsIntegration: 'Field Operations Integration',
              WhatsAppChatbot: 'WhatsApp Chatbot',
              AuditAlertScope: 'Audit Alert Scope',
              AuditAlertScopeDesc: 'Select which products to enable audits for',
              AuditAlertPrioritization: 'Audit Alert Prioritization',
              AuditAlertPrioritizationDesc: 'Select which products should be prioritized for audits',
              AuditAlertFrequency: 'Audit Alert Frequency',
              AuditAlertFrequencyDesc: 'How often should audits be performed',
              AuditAlertDelivery: 'Audit Alert Delivery',
              AuditAlertDeliveryDesc: 'Select how audit alerts should be delivered',
              DaysSinceLastSalesThreshold: 'Days Since Last Sales Threshold',
              DaysSinceLastSalesThresholdDesc: 'Number of days since last sale to trigger an alert',
              DefaultValueDays: 'Default Value (Days)',
              SalesSensitivityProbability: 'Sales Sensitivity Probability',
              SalesSensitivityProbabilityDesc: 'Configure the probability threshold for sales sensitivity',
            },
            LockIconTooltip: "To edit the configuration. Please contact your Bops' Account Operator",
          },
          SupplierOnboarding: {
            Badge: 'Setting up your panel',
            WelcomeWithName: 'Welcome, {{name}}.',
            WelcomeGeneric: 'Welcome to Bops.',
            Description: 'We’re processing your data. Your dashboard will be ready in less than',
            DescriptionTimeframe: '24 hours',
            EmailWhenReady: 'We’ll email you as soon as your panel is ready.',
            NotifyMe: 'Notify me when ready',
            ViewTraining: 'View training',
            PreviewLabel: 'Preview of your dashboard',
            PanelIncludes: 'What your panel includes',
            Steps: {
              ConnectedLabel: 'Data sources connected',
              ConnectedDetail: 'Credentials verified',
              ProcessingLabel: 'Processing data',
              ProcessingDetail: 'Sales, inventory, and distribution',
              ReadyLabel: 'Dashboard ready',
              ReadyDetail: 'KPIs, alerts, and reports available',
            },
            Features: {
              DashboardLabel: 'Dashboard',
              DashboardDescription: 'Sales, inventory, and availability KPIs at a glance',
              AlertsLabel: 'Alerts',
              AlertsDescription: 'Trend indicators that catch problems before they happen',
              ChainSummaryLabel: 'Chain summary',
              ChainSummaryDescription: 'Interactive analysis by chain and product with drill-down',
              ExportLabel: 'Export data',
              ExportDescription: 'CSV download at store-product-week level for Excel',
            },
          },
          FieldService: {
            Title: 'Field Services',
            Loading: "Hang on we're gathering your field services...",
            LoadingDetail: "Hang on we're gathering this field service...",
            BackToList: 'Field Services',
            ModeViewer: 'Viewer',
            ModeEdit: 'Edit',
            ModeAriaLabel: 'View mode',
            // Shared placeholder for any contract field the client has not filled in yet.
            NotConfigured: 'Not configured',
            List: {
              FieldService: 'Field Service',
              Stores: 'Stores covered',
              StoresCount_one: '{{count}} store',
              StoresCount_other: '{{count}} stores',
              TaskTypes: 'Task Types',
              EmptyTitle: 'No field services yet',
              EmptyDescription: 'Field services contracted by this client will show up here.',
            },
            TaskTypeName: {
              pog: 'Planogram Audit',
              price: 'Price Audit',
              shelf: 'Shelf Audit & Execution',
              promoae: 'Promo/Display Audit & Execution',
              promo: 'Promo/Display Deployment',
            },
            TaskTypeShort: {
              pog: 'Planogram',
              price: 'Price',
              shelf: 'Shelf',
              promoae: 'Promo Audit',
              promo: 'Promo/Display',
            },
            Sections: {
              Coverage: 'Coverage',
              Availability: 'Availability',
              AvailabilityDesc: 'How often this field service can service covered stores.',
              TaskTypes: 'Task Types',
              Workers: 'Workers',
            },
            Contract: {
              TaskVolumeDesc: 'Target number of tasks per visit block.',
              TasksPerVisit: 'Tasks per visit',
              VisitsPerPeriod: 'Visits per period',
              ApprovedTaskTypesDesc: 'Work this field service is approved to perform.',
            },
            ContractStatus: {
              active: 'Active',
              ended: 'Ended',
            },
            Availability: {
              Daily: 'Daily',
              DailySub: 'Every business day',
              Weekly: 'Weekly',
              Monthly: 'Monthly',
              WeeklyHeadline_one: 'Up to {{count}} visit per week',
              WeeklyHeadline_other: 'Up to {{count}} visits per week',
              MonthlyHeadline_one: 'Up to {{count}} visit per month',
              MonthlyHeadline_other: 'Up to {{count}} visits per month',
              Sub: 'On covered stores',
              LeadTime_one: '{{count}} day lead time',
              LeadTime_other: '{{count}} days lead time',
            },
            Workers: {
              Description: 'Field team members employed through this field service.',
              EmptyTitle: 'No workers yet',
              EmptyDescription: 'This field service has no workers on its roster yet.',
            },
            WorkerStatus: {
              on: 'On',
              off: 'Off',
              idle: 'Idle',
            },
            Coverage: {
              Description:
                'Contracts are per retailer. A retailer is available once at least one of its stores is covered.',
              TotalStores_one: '{{count}} store',
              TotalStores_other: '{{count}} stores',
              StoresCovered_one: '{{count}} covered',
              StoresCovered_other: '{{count}} covered',
              Available: 'Available',
              Unavailable: 'Unavailable',
              ViewStores: 'View stores',
              ModalTitlePrefix: 'Stores',
              ModalSubtitle: 'Stores this field service covers.',
              SearchPlaceholder: 'Search stores by name…',
              NoMatches: 'No stores match this search.',
              LoadError: 'Could not load stores.',
              ModalCount: '{{covered}} covered · showing {{matched, number}} of {{total, number}}',
              PagerAriaLabel: 'Store pagination',
              PreviousPage: 'Previous page',
              NextPage: 'Next page',
              CloseModal: 'Close',
            },
          },
          Schedule: {
            Title: 'Schedules',
            Loading: "Hang on we're gathering your schedules...",
            SearchPlaceholder: 'Search schedules by name…',
            Empty: {
              Title: 'No schedules yet',
              Description: 'Schedules you create will show up here.',
              NoResultsTitle: 'No matching schedules',
              NoResultsDescription: 'Try another search or clear the filters.',
            },
            CreateButton: 'Create schedule',
            CreateNoPermission: "You don't have permission to create schedules.",
            RefreshError: 'Could not refresh the page. Reload it to see the latest changes.',
            Wizard: {
              Title: 'Build a schedule',
              BackToList: 'Schedules',
              Back: 'Back',
              Next: 'Next',
              Finish: 'Create schedule',
              CreateSuccess: 'Schedule created',
              CreateError: 'Could not create the schedule. Please try again.',
              AlertsOnlyScheduleName: 'Alerts Only Schedule',
              PrioritizeByWeekScheduleName: 'Weekly Priorities Schedule',
              NewScheduleName: 'New Schedule',
              EligibleFieldServices_one: '{{count}} eligible field service',
              EligibleFieldServices_other: '{{count}} eligible field services',
              Steps: {
                where: 'Where',
                what: 'What',
                frequency: 'Frequency',
                firm: 'Field service',
              },
              Where: {
                RetailersLabel: 'Retailers',
                AllRetailers: 'All retailers',
                SectionSubtitle:
                  'Select a whole retailer or one of its regions on the left, or pick individual stores.',
                OneRetailerHint: 'One retailer per schedule',
                StoreSearchPlaceholder: 'Search stores by name or region…',
                SelectAll: 'Select all {{count}}',
                ClearAll: 'Clear these',
                StoresSelected_one: '{{count}} selected',
                StoresSelected_other: '{{count}} selected',
                NoStores: 'No stores match this filter.',
                ColumnStore: 'Store',
                ColumnRegion: 'Region',
                ShowingCount: 'Showing {{shown}} of {{total}} stores',
              },
              What: {
                SectionSubtitle: 'Two decisions: what triggers the work, and what the work is.',
                WorkMixLabel: 'What kind of work',
                WorkMixAlertsOnly: 'Alerts only',
                WorkMixAlertsOnlyDesc: 'Live alerts fill the whole visit.',
                WorkMixScheduleOnly: 'Schedule only',
                WorkMixScheduleOnlyDesc: 'Scheduled tasks only, no alerts.',
                WorkMixBoth: 'Alerts and schedule',
                WorkMixBothDesc: 'Alerts get a capped share; scheduled tasks take the rest.',
                AlertsTitle: 'Alerts',
                AlertsExplainAlertsOnly:
                  'No schedule attached. Every visit opens its full time to live alerts, ranked by recovered value.',
                AlertsExplainBoth:
                  'Claims a capped share of visit time for live out-of-stock, low-stock, missing-display, and planogram-drift alerts.',
                AlertCeilingDefaultLabel: 'Default ceiling: {{pct}}% of the visit',
                AlertCeilingDefaultNote: 'Scheduled tasks take the remaining {{remaining}}% · can be changed per week',
                ScheduledTasksLabel: 'Scheduled tasks',
                CategoriesLabel: 'Categories',
                NoCategories: "No categories found for this retailer's products.",
                MissingCategoriesWarning:
                  'Select at least one category so these task types have products to audit: {{taskTypes}}',
                TaskTypesSelected_one: '{{count}} task type selected',
                TaskTypesSelected_other: '{{count}} task types selected',
                TaskTypesNoneSelected: 'None selected',
                DecideWeeklyLabel: 'Prioritize by week',
                DecideWeeklyDesc: 'Instead of fixing the list now, pick the tasks each week',
                DecideWeeklyChosenEachWeek: 'Chosen each week',
                DecideWeeklyNoteScheduleOnly:
                  'No standing task list. You pick the tasks for each visit week by week. Edit the schedule anytime to fix a list.',
                DecideWeeklyNoteBoth:
                  'No scheduled tasks yet. Alerts fill each visit until you add some. Edit the schedule anytime to bring tasks in.',
              },
              Frequency: {
                SectionSubtitle: 'How often should these visits run?',
                CadenceOnce: 'Once',
                CadenceOnceDesc: 'A single one-off visit on a chosen date',
                CadenceWeekly: 'Weekly',
                CadenceWeeklyDesc: 'Repeats on the weekdays you pick',
                CadenceBiWeekly: 'Bi-weekly',
                CadenceBiWeeklyDesc: 'Repeats on the weekdays you pick, every other week',
                CadenceMonthly: 'Monthly',
                CadenceMonthlyDesc: 'Repeats on chosen days each month',
                WeekdaysLabel: 'On these days',
                MonthlyRepeatsOnLabel: 'Repeats on',
                MonthlyOrdinalPrefix: 'The',
                MonthlyOrdinalSuffix: 'of the month',
                MonthlyOrdinalFirst: 'first',
                MonthlyOrdinalSecond: 'second',
                MonthlyOrdinalThird: 'third',
                MonthlyOrdinalFourth: 'fourth',
                MonthlyOrdinalLast: 'last',
                MonthlyOrdinalNote: 'Runs on the {{ordinalLabel}} {{weekday}} of each month',
                VisitDateLabel: 'Visit date',
                VisitDateNote: 'A single visit will be generated on this date.',
                StartDateLabel: 'Start date',
                EndDateLabel: 'End',
                EndDateNone: 'No end date',
                EndDatePreset: 'End after',
                EndDateCustom: 'End on a specific date',
                WeeksSuffix_one: '{{count}} week',
                WeeksSuffix_other: '{{count}} weeks',
              },
              Firm: {
                Title: 'Field service',
                SectionSubtitle:
                  'Choose who runs the visits. Shown here: field services with an active contract for the retailers you selected.',
                NameLabel: 'Schedule name',
                NameRequired: 'Name the schedule to create it.',
                ModeSwitcherAriaLabel: 'Field service assignment mode',
                ModeSingle: 'One field service',
                ModeSplit: 'Split',
                LoadingEligible: 'Checking which field services are eligible…',
                NoEligible:
                  'No field service has an active contract for these retailers yet. Go back and adjust the stores, or add a new field service.',
                NoSharedTaskType:
                  'No task type is approved by every selected field service, so this schedule could never get objectives. Pick field services that share at least one approved task type.',
                EligibilityError:
                  "Couldn't check field service eligibility. Try changing a selection to retry, or come back later.",
                AssignPlaceholder: 'Assign field service…',
                NoCoveragePlaceholder: 'No coverage',
                PartialCoverageBanner: 'Covers {{covered}} of {{total}} requested stores. Not covered: {{stores}}',
                PartialCoverageMoreStores: '{{stores}} and {{count}} more',
                ClearAssignment: 'Clear assignment',
                SplitShowingRange: 'Showing {{from}}–{{to}} of {{total}} stores',
                SplitPagerAriaLabel: 'Store assignment pagination',
                PreviousPage: 'Previous page',
                NextPage: 'Next page',
              },
            },
            Filters: {
              Status: 'Status',
              FieldService: 'Field Service',
              AllFieldServices: 'All field services',
            },
            StatusFilter: {
              all: 'All',
              active: 'Active',
              paused: 'Paused',
              ended: 'Ended',
            },
            ViewToggle: {
              AriaLabel: 'Schedules view',
              Table: 'Table view',
              Cards: 'Card view',
            },
            Card: {
              Stores: 'Stores',
              StoresCount_one: '{{count}} store',
              StoresCount_other: '{{count}} stores',
            },
            Delete: {
              Action: 'Delete schedule',
              Confirm:
                'Delete the schedule "{{name}}"? Its objectives and weeks are deleted too. This cannot be undone.',
              Error: 'Could not delete the schedule. Please try again.',
            },
            List: {
              RowActions: 'Actions for {{name}}',
              FieldServices: 'Field Services',
              Name: 'Schedule',
              Scope: 'Scope',
              Cadence: 'Cadence',
              Status: 'Status',
            },
            RecurrenceLabel: '{{cadence}} · {{recurrence}}',
            MonthlyRecurrence: '{{ordinal}} {{weekday}}',
            CadenceLabel: {
              once: 'Once',
              weekly: 'Weekly',
              bi_weekly: 'Bi-weekly',
              monthly: 'Monthly',
            },
            Status: {
              active: 'Active',
              paused: 'Paused',
              ended: 'Ended',
            },
            Configuration: {
              Eyebrow: 'Configuration',
              BackToSchedule: 'Back to schedule',
              ExcelSource: 'Excel upload',
              Settings: 'Settings',
              Pause: 'Pause',
              Resume: 'Resume',
              End: 'End schedule',
              EndConfirm:
                'Ending {{name}} stops every future visit and makes it read-only. This cannot be undone. Do you want to continue?',
              PausedToast: 'Paused {{name}}',
              ResumedToast: 'Resumed {{name}}',
              EndedToast: 'Ended {{name}}',
              StatusError: "Couldn't change the schedule status",
              Tiles: {
                Stores: 'Stores',
                TaskTypes: 'Task types',
                Cadence: 'Cadence',
                FieldServices: 'Field services',
              },
              Name: {
                Label: 'Schedule name',
                Help: 'Shown on the Schedules list and to the field team',
              },
              Cadence: {
                Label: 'Cadence',
                Dates: 'From {{start}} to {{end}}',
                NoEnd: 'no end date',
                Edit: 'Change',
                Saved: 'Cadence updated',
                SaveError: "Couldn't save the cadence",
                LoadError: "Couldn't load the visit calendar",
                NotEditableLegacy: 'This cadence was saved in an older format and cannot be edited here',
                LegendVisit: 'Scheduled run',
                LegendToday: 'Today',
              },
              Upcoming: {
                Title: 'Upcoming',
                PlannedVisits: 'planned visits in the next {{weeks}} weeks',
                Next: 'Next visit: {{date}}',
                None: 'No visit scheduled in this window',
              },
              TaskTypes: {
                Title: 'Task types',
                Empty: 'No task type configured yet',
                LoadError: "Couldn't load the task types",
              },
              Ceiling: {
                Label: 'Alert ceiling',
                NotApplicable: 'Does not apply',
                Edit: 'Change',
                Save: 'Save',
                Cancel: 'Cancel',
                CurrentWeekKeptHint:
                  'The week in progress keeps the ceiling it has now; the change applies from the next week.',
                SwitchConfirm: 'Week ceilings set for future weeks will be reset. Do you want to continue?',
                SaveError: "Couldn't save the alert ceiling",
              },
              CeilingNote: {
                hybrid:
                  'Alerts fill up to this share of the visit. Scheduled objectives take whatever is left, never below the floor.',
                alerts_only: 'Alerts only. With no proactive work to protect, the whole visit goes to alerts.',
                scheduled_only: 'Scheduled work only. No alerts feed this schedule.',
              },
              Conflict: {
                schedule_ended: 'This schedule has ended. Only its name can change.',
                ended_status_change: 'An ended schedule cannot change status.',
                not_started: 'This schedule has not started yet. Delete it instead.',
                started_weeks_changed:
                  'This change would drop weeks that have already started. End this schedule and create a new one.',
                schedule_expired: 'This schedule has expired. Create a new one.',
                past_visit_added: 'This change would add a visit on a date that has already passed.',
                composition_published: 'This schedule has published objectives, so its type can no longer change.',
                published_weeks_dropped:
                  'A week with published objectives would stop being a visit week. Keep it in the cadence.',
                published_objectives: 'This schedule has published objectives, so it can no longer change.',
                no_objectives:
                  'A schedule-only schedule needs at least one objective. Add one before changing the type.',
              },
              VisitTarget: {
                Label: 'Visit target size',
                Help: 'Questions a rep can complete in one visit at this duration',
                NotSet: 'Not set',
                Value: '{{tasks}} tasks',
              },
              Stores: {
                Title: 'Stores · {{stores}} stores',
                ViewMore: 'View more',
                Count: '{{stores}} stores',
                ModalTitle: 'Stores · {{retailer}}',
                ModalSubtitle: 'Stores on this schedule.',
                SearchPlaceholder: 'Search stores by name…',
                Empty: 'No stores match this search.',
                LoadError: "Couldn't load the stores of this schedule.",
                Inactive: 'Inactive',
                Footer: '{{shown}} of {{total}} stores',
                Done: 'Done',
              },
            },
            Detail: {
              Loading: "Hang on we're gathering this schedule...",
              BackToList: 'Schedules',
              Configuration: 'Configuration',
              Eyebrow: 'Merchandising objectives',
              Send: 'Send',
              SendComingSoon: 'Sending is coming soon.',
              NoTasks: 'No tasks in this schedule yet.',
              DraftFromLastWeek: 'Draft from last week',
              CopyScheduleList: "Copy the schedule's list",
              CustomizeSuccess: 'Week list created',
              CustomizeError: "Couldn't create the week list",
              LockedBadge: 'Published · locked',
              StartedBadge: 'Current week · read-only',
              EndedBadge: 'Schedule ended · read-only',
              Rename: {
                Edit: 'Rename schedule',
                Input: 'Schedule name',
                Save: 'Save name',
                Cancel: 'Cancel renaming',
                Error: 'Could not rename the schedule. Please try again.',
              },
              Metadata: {
                Cadence: 'Cadence',
                Retailer: 'Retailer',
                Stores: 'Stores',
                FieldServices: 'Field Services',
                FirmWithStoreCount: '{{name}} ({{storeCount}})',
                None: '—',
              },
              Filters: {
                Area: 'Area',
                AllAreas: 'All areas',
              },
              Task: {
                Priority: 'Priority',
                Area: 'Area',
                Category: 'Category',
                NoCategory: '—',
                TaskType: 'Task Type',
                ReviewStatus: 'Review Status',
                ReviewStatusAriaLabel: 'Review status for {{taskType}}',
                MayNotFit: 'May not fit',
                NotContracted: 'Not contracted',
                NotContractedHint:
                  'Not every field service of this schedule approves this task type, so it takes no slot in the visit and is not counted as pending.',
                RemoveAriaLabel: 'Remove {{taskType}}',
              },
              ReviewStatus: {
                pending: 'Pending',
                approved: 'Approved',
                rejected: 'Rejected',
              },
              ReviewStatusPublished: 'Published',
              ReviewStatusUpdateError: 'Could not update the review status. Please try again.',
              PriorityUpdateError: 'Could not update the priority. Please try again.',
              RemoveTaskError: 'Could not remove the objective. Please try again.',
              RemoveTaskConfirm: 'Remove the objective {{taskType}}?',
              RemoveTaskConfirmWithCategory: 'Remove the objective {{taskType}} · {{category}}?',
              ListRefreshError: 'Could not refresh the objectives. Reload the page to see the latest changes.',
              ApprovePendingButton_one: 'Approve {{count}} pending',
              ApprovePendingButton_other: 'Approve {{count}} pending',
              ApprovePendingConfirm_one: 'Approve {{count}} pending task?',
              ApprovePendingConfirm_other: 'Approve {{count}} pending tasks?',
              ApprovePendingSuccess_one: '{{count}} task approved',
              ApprovePendingSuccess_other: '{{count}} tasks approved',
              ApprovePendingError: 'Could not approve the pending tasks. Please try again.',
              AddTask: {
                Button: 'Add objective',
                Title: 'Add an objective',
                TaskType: 'Task type',
                TaskTypePlaceholder: 'Select a task type…',
                Categories: 'Categories',
                CategoriesPlaceholder: 'Select categories…',
                CategoriesLoading: 'Loading categories…',
                CategoriesError: 'Could not load categories. Please try again.',
                CategoriesHint: 'Products in the selected categories will be audited by this task.',
                CategoriesSelectTypeFirst: 'Pick a task type first',
                CategoriesOptional: 'Optional for this task type.',
                NoPlannableTaskTypes: 'No task type is approved by every field service of this schedule.',
                Area: 'Area',
                AreaPlaceholder: 'e.g. Grocery, Dairy…',
                Submit: 'Add objective',
                Success: 'Task added',
                Error: 'Could not add the task. Please try again.',
              },
              Sidebar: {
                Collapse: 'Collapse sidebar',
                Expand: 'Expand sidebar',
                Resize: 'Resize sidebar',
                ScheduleLabel: 'Schedule',
                ObjectiveTitle: 'Objective',
                Composition: 'Composition',
                AlertCeiling: 'Alert ceiling',
                CompositionValue: {
                  scheduled_only: 'Scheduled only',
                  alerts_only: 'Alerts only',
                  hybrid: 'Alerts + scheduled',
                },
                AlertCeilingDoesNotApply: 'Does not apply',
                AlertCeilingPercent: '{{value}}% of the visit',
                Period: {
                  WeekOf: 'Wk of {{date}}',
                  PositionApproved: 'Week {{current}} of {{total}} · approved',
                  PositionStarted: 'Week {{current}} of {{total}} · in progress',
                  PositionPending_one: 'Week {{current}} of {{total}} · {{count}} pending',
                  PositionPending_other: 'Week {{current}} of {{total}} · {{count}} pending',
                  NoWeeks: 'No upcoming visit weeks',
                  LoadError: 'Could not load the visit weeks.',
                  PreviousAriaLabel: 'Previous period',
                  NextAriaLabel: 'Next period',
                },
                Utilization: {
                  Percent: '{{value}}%',
                  Cap: 'Cap',
                  Reset: 'Reset',
                  CustomizedThisWeek: 'Customized this week',
                  SaveError: "Couldn't save the alert ceiling",
                  TickSlots: '{{capSlots}} of ~{{typicalSlots}} slots',
                  CappedFirstLegend_one: '{{count}} alert capped first',
                  CappedFirstLegend_other: '{{count}} alerts capped first',
                  ScheduledLegend_one: '{{count}} merch objective',
                  ScheduledLegend_other: '{{count}} merch objectives',
                  InfoTitle: 'How the ceiling works',
                  MayNotFitCount_one: '{{count}} may not fit',
                  MayNotFitCount_other: '{{count}} may not fit',
                  InfoNote: 'Alerts are calculated and capped first. These objectives fill the room left over.',
                  InfoContractNote:
                    'Visit size averages {{typicalSlots}} tasks and varies with duration, so slot counts are approximate. Each week can override the schedule default.',
                },
              },
            },
          },
        },
      },
      es: {
        translation: {
          Sidebar: {
            Main: 'Recomendaciones',
            Monitor: 'Portal',
            SecondaryMonitor: 'Administrativo',
            Overstock: 'Exceso Inventario',
            AskBops: 'Agente CPFR',
            Configuration: 'Configuración',
            Logout: 'Salir',
            Toolkit: 'Recursos',
            Documentation: 'Documentación',
            DocumentationPage: 'Página {{number}}',
            Insights: 'Eventos',
            TrainingVideos: 'Videos',
            Certification: 'Certificación',
            ResetDashboard: 'Reiniciar portal a su estado original',
            Credentials: 'Credenciales',
            Chains: 'Cadenas',
            Tools: 'Herramientas',
            Gpc: 'Buscador GPC',
            Syncfonia: 'Syncfonía+',
            Verified: 'Verified',
            Users: 'Usuarios',
            Suppliers: 'Proveedores',
            CredentialsSummary: 'Resumen de Credenciales',
            Collapse: 'Colapsar',
            ManualUpload: 'Carga Manual',
            Master: 'Archivos Maestros',
            Policies: 'Políticas',
            Policy: 'CPFR',
            CPFR: 'CPFR',
            Integrations: 'Integraciones',
            Store: 'Tienda',
            Schedules: 'Cronogramas',
            FieldServices: 'Servicios de Campo',
          },
          Changelog: {
            ChangeLog: 'Novedades',
            PopUpTitle: '¡Lo Nuevo en Bops!',
            EmptyEntries: '¡No hay actualizaciones disponibles por el momento!',
            EmptyDetails: 'No hay detalles disponibles.',
          },
          Documentation: {
            LoadMoreButton: 'Cargar más...',
            EmptyDocumentationPage: 'No hay documentación disponible para esta página.',
            EmptyDataBase: 'No hay datos disponibles para esta base de datos.',
          },
          Guides: {
            Wrl: {
              badge: 'Guía de integración',
              title: 'Conectar Bops con Walmart Retail Link',
              subtitle:
                'Bops puede conectarse y extraer datos de Walmart Retail Link a través de una <bold>cuenta de bot dedicada</bold>. Esta guía cubre la configuración inicial de la cuenta y la actualización periódica de sus credenciales.',
              Prereqs: {
                title: 'Requisitos',
                description: 'Antes de comenzar, define las dos personas y cuentas involucradas en el proceso.',
                Admin: {
                  title: 'Administrador de WRL',
                  description:
                    'Responsable de crear la cuenta de bot dedicada para Bops dentro de Walmart Retail Link.',
                },
                Bot: {
                  title: 'Cuenta de bot de WRL',
                  descriptionPre: 'Elige el correo a utilizar para el bot, p. ej. ',
                  descriptionPost: '. Este usuario se encarga de:',
                  duty1: 'Crear la contraseña inicial de la cuenta.',
                  duty2:
                    'Administrar la contraseña y el token dentro de Bops, ya que Walmart obliga a que ambos expiren cada ~60 días.',
                },
              },
              Configure: {
                title: 'Configurar la cuenta de bot',
                description: 'El proceso para crear una nueva cuenta de bot en Walmart Retail Link, paso a paso.',
                s1: {
                  title: 'Acceder a Walmart Retail Link',
                  body: 'El Administrador de WRL debe iniciar sesión en Walmart Retail Link.',
                  imgAlt: 'Pantalla de inicio de sesión de Walmart Retail Link',
                  imgCaption: 'Pantalla de inicio de sesión de Walmart Retail Link.',
                },
                s2: {
                  title: 'Abrir la administración de usuarios',
                  b1: 'Haz clic en las iniciales o la foto de perfil en la esquina superior derecha de la pantalla.',
                  b2: 'Selecciona <bold>Administración de usuarios</bold> (User Management).',
                  imgAlt: 'Menú de perfil con la opción User Management',
                  imgCaption: 'El menú de perfil abre User Management.',
                },
                s3: {
                  title: 'Agregar un nuevo usuario',
                  body: 'Haz clic en <bold>Agregar usuario</bold> (Add User) en la esquina superior derecha.',
                  imgAddAlt: 'Botón Add User en User Management',
                  imgAddCaption: 'El botón Add User se encuentra arriba a la derecha.',
                  formIntro: 'Completa los campos obligatorios para el nuevo usuario:',
                  fields: {
                    name: {
                      label: 'Nombre / Apellido',
                      value: 'Nombre y apellido del propietario de la cuenta de bot.',
                    },
                    email: {
                      label: 'Correo electrónico',
                      value: 'El correo seleccionado en los pasos preliminares.',
                    },
                    phone: {
                      label: 'Número de teléfono',
                      value: 'El número de teléfono del propietario.',
                    },
                    botAccount: {
                      label: 'Cuenta de bot',
                      value: 'Marca la casilla <bold>Esta es una cuenta de bot</bold> (This is a bot user account).',
                    },
                    employeeType: {
                      label: 'Tipo de empleado',
                      value: 'Empleado Directo (Direct Employee).',
                    },
                    position: {
                      label: 'Puesto',
                      value: 'La descripción que mejor se ajuste al rol.',
                    },
                    role: {
                      label: 'Rol',
                      value: 'Usuario adicional con acceso completo (Full Access Additional User).',
                    },
                    supplier: {
                      label: 'Proveedor',
                      value: 'El proveedor correspondiente del menú desplegable.',
                    },
                  },
                  submit:
                    'Por último, haz clic en <bold>Agregar nuevo usuario</bold> (Add New User) para finalizar la creación de la cuenta.',
                  imgFormAlt: 'Formulario de creación de usuario con la casilla de bot marcada',
                  imgFormCaption: 'Formulario completo con la casilla This is a Bot User Account marcada.',
                  imgSuccessAlt: 'Mensaje de confirmación de usuario agregado',
                  imgSuccessCaption: 'Confirmación: el usuario fue agregado correctamente.',
                },
                s4: {
                  title: 'Revisar la cuenta en la lista de usuarios activos',
                  b1: 'Ve a la lista de <bold>Cuentas Activas</bold> (Active Account List).',
                  b2: 'Localiza la nueva cuenta de bot.',
                  b3: 'Haz clic en el icono de los tres puntos (…) al lado de la cuenta.',
                  b4: 'Selecciona <bold>Ver perfil</bold> (View Profile).',
                  imgAlt: 'Lista de usuarios activos con el menú View Profile',
                  imgCaption: 'El menú de tres puntos abre View Profile.',
                },
                s5: {
                  title: 'Generar y copiar el token',
                  body: 'Dentro del perfil de la cuenta, genera y copia el <bold>token del bot</bold>.',
                  imgAlt: 'Perfil del bot con el botón Generate Bot Token',
                  imgCaption: 'Usa Generate Bot Token y copia el token con el icono de copiar.',
                },
              },
              Register: {
                title: 'Configurar la contraseña y registrar en Bops',
                tocLabel: 'Contraseña y registro en Bops',
                description:
                  'El propietario de la cuenta de bot finaliza la conexión registrando las credenciales en Bops.',
                step1:
                  'El propietario recibirá un correo de Walmart Retail Link confirmando que la cuenta fue creada e indicando que debe configurar la contraseña.',
                step2:
                  'Una vez configurada la contraseña, debe ingresar a la sección <bold>Credenciales</bold> de la plataforma Bops y registrar sus credenciales de WRL.',
                step3Intro: 'En esa sección, el propietario debe:',
                check1: 'Aceptar los Términos y Condiciones.',
                check2: 'Ingresar su información personal.',
                check3: 'Registrar el nombre de usuario, contraseña y token de su cuenta de WRL.',
                noteTitle: 'Nota importante',
                note: 'La contraseña y el token deben actualizarse aproximadamente <bold>cada 60 días</bold> para mantener la extracción de datos sin interrupciones.',
              },
              Update: {
                title: 'Actualizar credenciales existentes',
                tocLabel: 'Actualizar credenciales',
                description:
                  'Cuando la contraseña o el token de la cuenta de bot expiran, actualiza <bold>ambas credenciales</bold> para garantizar un acceso ininterrumpido.',
                s1: {
                  title: 'Acceder a la cuenta de administrador de WRL',
                  body: 'Inicia sesión en Walmart Retail Link con la cuenta de administrador.',
                },
                s2: {
                  title: 'Abrir la administración de usuarios',
                  body: 'Desde el menú de perfil, selecciona <bold>Administración de usuarios</bold> (User Management).',
                },
                s3: {
                  title: 'Seleccionar el usuario de la cuenta de bot',
                  body: 'Usa el buscador para encontrar la cuenta de bot de WRL que se utiliza para la extracción periódica de datos.',
                },
                s4: {
                  title: 'Actualizar el token',
                  body: 'Dentro del perfil de la cuenta, haz clic en <bold>Generar Token</bold> para actualizar la credencial, y luego cópiala con el icono de copiar.',
                  imgAlt: 'Generar y copiar el token del bot',
                  imgCaption: 'Generate Bot Token regenera el token; cópialo con el icono.',
                },
                s5: {
                  title: 'Cambiar la contraseña de la cuenta de bot',
                  b1: 'Regresa a la página de inicio de sesión y haz clic en <bold>Forgot Password</bold> / Olvidé mi contraseña.',
                  b2: 'Ingresa la contraseña actual y la nueva contraseña para la cuenta de bot, y confirma tu selección.',
                  b3: 'Recibirás un correo de validación en la dirección de la cuenta de bot.',
                  b4: 'Haz clic en el enlace del correo para confirmar el cambio de contraseña.',
                },
                s6: {
                  title: 'Compartir las credenciales nuevas con Bops',
                  body: 'Con el token y la contraseña ya actualizados, ingresa a la plataforma Bops en la sección <bold>Credenciales</bold> y actualiza ambas credenciales para reanudar la extracción de datos.',
                  imgAlt: 'Sección de Credenciales en la plataforma Bops',
                  imgCaption:
                    'Sección Credentials de la plataforma Bops, con la conexión de Walmart Retail Link activa.',
                },
              },
            },
            Footer: {
              label: 'Guía de integración',
              support: '¿Dudas? Escríbenos a',
              tocAriaLabel: 'Tabla de contenidos',
              tocLabel: 'En esta guía',
            },
          },
          Global: {
            ErrorMessage:
              'Algo ha salido mal.\nNuestro equipo está trabajando para solucionarlo. Si necesitas ayuda, contacta a tu account operator o escríbenos a support@bops.ai.',
            WaitingMessage: 'Por favor espera...\nEstamos obteniendo tu data',
            UnathorizedMessage:
              'No tienes permiso para ver esta página.\nSi piensas que esto es un error contáctanos a support@bops.ai.',
            PageNotFoundMessage:
              'Página no encontrada.\nLa página que buscas no está disponible.\nHaz clic abajo para ir al portal principal.',
            NoAccountMessage:
              '¡Parece que tu acceso aún no se encuentra configurado!\n\nPonte en contacto con el administrador de cuenta para completar la configuración\ny obtener acceso a tus recomendaciones de inventario.',
            GoHome: 'Ir al Portal principal',
            GoBack: 'Volver',
            TryAgain: 'Intentar de nuevo',
            Close: 'Cerrar',
            WorkInProgressTitle: 'Casi listo',
            WorkInProgressMessage:
              'Estamos dando los últimos toques a esta sección. Te avisaremos en cuanto esté disponible.',
            Confirm: 'Confirmar',
            Canceled: 'Cancelar',
            Select: 'Seleccionar',
            Required: 'es requerido',
            Page: 'Página',
            Of: 'de',
            Next: 'Siguiente',
            Previous: 'Anterior',
            WelcomeMessage: 'Bienvenido a tu',
            WelcomeMessage2: 'Plataforma de Operaciones Empresariales',
          },
          Recommendation: {
            Loading: 'Por favor espera...\nEstamos cargando tus recomendaciones',
            ImportantOverstock: 'Exceso de Inventario Importante',
            Overstock: 'Exceso de Inventario',
            PotentialOverstock: 'Posible Exceso de Inventario',
            PotentialUnderstock: 'Posible Bajo Inventario',
            Understock: 'Bajo Inventario',
            PotentialStockout: 'Posible Inventario Agotado',
            StoreStockout: 'Inventario Agotado en Tienda',
            CompleteStockout: 'Inventario Agotado',
            InventoryEfficiencyScore: 'Score de Eficiencia de Inventario',
            InventoryDays: 'Días de Inventario',
            Dashboard: 'Portal',
            WelcomeMessageHi: 'Hola',
            WelcomeMessageHere: 'Aquí',
            WelcomeMessageFinal:
              ' este es un resumen de tu score actual y {{length}} recomendaciones que ayudarán a simplificar tu toma de decisiones en cuanto a inventario.',
            TopRecommendations: 'Top {{length}} Recomendaciones',
          },
          Monitor: {
            Loading: 'Por favor, espera... Estamos recopilando tu portal',
            DashboardNotFound:
              'El portal que estás buscando no existe. Selecciona uno desde el menú lateral o haz clic abajo para volver al portal principal.',
            WaitingScreen: {
              Generic: {
                Title: 'Estamos configurando tu plataforma',
                Subtitle: 'Estamos preparando tu espacio. En breve tendrás acceso a tus dashboards.',
              },
              AvanzadoDashboardPending: {
                Title: 'Estamos configurando tu plataforma',
                Subtitle: 'Estamos preparando tu espacio. En breve tendrás acceso a tus dashboards.',
              },
              PlanChangeValidation: {
                Title: 'Estamos validando tu cambio de plan',
                Subtitle: 'En breve confirmaremos tu nuevo plan y tendrás acceso a tus dashboards actualizados.',
              },
              ScopeExpansionReview: {
                Title: 'Estamos validando tu cambio de plan',
                Subtitle: 'Nuestro equipo está confirmando los detalles de tu cuenta antes de habilitar el nuevo plan.',
              },
            },
          },
          Configuration: {
            Loading: 'Por favor, espera... Estamos recopilando tus archivos de configuración',
            Title: 'Configuración de Archivos Maestro',
            Message:
              'Estos archivos se utilizan para controlar la configuración de tus recomendaciones de inventario.\nLas actualizaciones de configuración serán aplicadas al final del día hábil.',
            Open: 'Abrir',
            Download: 'Descargar',
            DownloadError: 'Error al descargar el archivo',
            Replace: 'Sustituir',
            Uploading: 'Subiendo',
            Upload: 'Subir',
            Cancel: 'Cancelar',
            LoadingLink: 'Por favor, espera... Estamos recopilando tus configuraciones',
            Error: 'Ha ocurrido un error al actualizar el archivo, por favor intenta de nuevo.',
            Success: 'Archivo subido correctamente!',
            All: 'Todas las configuraciones',
            LastUpdated: 'Ultima actualización',
            Name: 'Nombre',
            Status: 'Estado',
            Tags: 'Tags',
            Active: 'Activo',
            InProgress: 'Actualizando',
            Processing: 'Procesando',
            Configuring: 'Configurando',
            Failed: 'Error al procesar',
            NewMaster: 'Nuevo Master',
            AddMaster: 'Añadir nuevo maestro',
            ModalTitle: 'Nuevo Archivo Maestro',
            Connector: 'Source',
            MasterType: 'Nombre del Archivo Maestro',
            SelectConnector: 'Seleccionar source',
            SelectMasterType: 'Ingresar nombre del archivo',
            LoadingModal: 'Generando archivo, esto puede tardar unos minutos. No cierre esta ventana.',
            SuccessModal: 'Un nuevo archivo maestro ha sido generado correctamente!',
            ChooseFile: 'Seleccionar archivo',
            SelectConnectorError: 'Por favor, selecciona un source',
            SelectFileError: 'Por favor, seleccione un archivo para subir',
            SelectMasterTypeError: 'Por favor, ingrese un nombre para el archivo maestro',
            NewMasterError: 'Ha ocurrido un error al subir el nuevo archivo maestro, por favor intenta de nuevo.',
            ResourceLockedError: 'No se puede actualizar el archivo maestro porque otro usuario lo está editando.',
            Continue: 'Continuar',
            Back: 'Volver',
            GenericSource: 'Genérico',
            SourceHelper:
              'A qué source pertenecen los datos. Elige Genérico si no pertenecen a uno, o aplican para varios.',
            ConfirmTitle: 'Revisa antes de crear',
            ConfirmMessage: 'Así se creará el archivo maestro. Vuelve atrás si algo no cuadra.',
            ConfirmName: 'Nombre',
            ConfirmTag: 'Tag',
            ConfirmFile: 'Archivo',
            ConfirmCreate: 'Crear archivo maestro',
            PreviewTitle: 'Qué cambiaría este archivo',
            PreviewMessage: 'Vas a reemplazar el contenido de {{master}} con {{file}}.',
            PreviewRows: 'Filas',
            PreviewColumns: 'Columnas',
            PreviewColumnsWarning: 'Este cambio incluye un cambio de esquema.',
            PreviewNoChanges: 'No hay ninguna diferencia entre los dos archivos. Revisa que sea el que querías.',
            ConfirmReplace: 'Reemplazar archivo',
          },
          Credentials: {
            Loading: 'Por favor, espera... Estamos recopilando tus credenciales',
            Title: 'Credenciales',
            Subtitle: 'Actualiza las credenciales de tus proveedores de datos aquí',
            Username: 'Usuario',
            Password: 'Password',
            Token: 'Token',
            Update: 'Editar',
            Accept: 'Aceptar',
            Account: 'Cuenta',
            SasUrl: 'URL de Acceso SAS',
            Save: 'Actualizar',
            Cancel: 'Cancelar',
            Activate: 'Activar',
            TitleModal: 'Edita las credenciales',
            Name: 'Nombre',
            Added: 'Agregado',
            ExpiringSoon: 'credencial(es) expirando pronto',
            ExpiringSoonTooltip: 'La credencial esta por expirar pronto.',
            LastUpdated: 'Ultima actualización',
            Active: 'Activo',
            Paused: 'Pausado',
            Expired: 'Expirado',
            Processing: 'Procesando',
            ExpiredCount: 'credencial(es) expirada(s)',
            ExpiredTooltip: 'La credencial expiró',
            ExpiredAndExpiring: '{{expired}} credencial(es) expirada(s), {{expiring}} expirando pronto',
            ExpiredPopupPrefix:
              '{{count}} credencial(es) expirada(s).Esto puede resultar en que la información no esté actualizada',
            Here: 'Aquí',
            ExpiringPopupPrefix: '{{count}} credencial(es) expirando pronto',
            UpdateMessage: 'Actualiza la(s) credencial(es):',
            Pending: 'Sin realizar',
            AllCredentials: 'Todas las Credenciales',
            Status: 'Estado',
            UpdateSuccess: 'Las credenciales se han actualizado correctamente',
            LoadingModal: 'Chequeando si la configuracion de {{name}} esta bien',
            Error: 'Ha ocurrido un error al actualizar las credenciales, por favor intenta de nuevo.',
            RequestCredentials:
              '¡No tenemos información de tus productos!\n\nActualiza tus credenciales de Walmart y/o Soriana para que obtengas\nacceso a tu Inteligencia de Inventario.',
            RequestCredentialsButton: 'Configurar credenciales',
            HasCredentialsButton: 'Documentación',
            Success: 'Las credenciales se han actualizadas correctamente',
            HasCredentials:
              '¡Gracias por ingresar a Inventario Inteligente!\n\nEstamos procesando la información de tus productos para su análisis.\nEn caso de tener alguna duda, por favor contactar al equipo de soporte presente en la documentación.',
            ErrorInvalidCredentials: 'Las credenciales son inválidas o han expirado. Verifícalas e intenta de nuevo.',
            StatusReason: {
              RpaBotBlocked:
                'Las credenciales no son válidas para el acceso automático, revise con su administrador el estatus de su cuenta. Para cualquier duda consulte el manual de credenciales: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
              UserBlocked:
                'El usuario está bloqueado, revise con su administrador el estatus de su cuenta. Para cualquier duda consulte el manual de credenciales: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
              InvalidUser:
                'El usuario ingresado no es válido, revise las credenciales e intente nuevamente. Para cualquier duda consulte el manual de credenciales: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
              InvalidPassword:
                'La contraseña ingresada no es válida, intente nuevamente. Para cualquier duda consulte el manual de credenciales: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
              PasswordExpired:
                'La contraseña ingresada expiró, actualice la contraseña y el token directamente desde la plataforma de WRL. Para cualquier duda consulte el manual de credenciales: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
              PasswordChangeRequired:
                'Se requiere un cambio de contraseña, actualice la contraseña y el token directamente desde la plataforma de WRL. Para cualquier duda consulte el manual de credenciales: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
              CredentialsExpired:
                'Las credenciales expiraron, actualice la contraseña y el token directamente desde la plataforma de WRL. Para cualquier duda consulte el manual de credenciales: https://app.gs1mex.bops.ai/guides/wrl#sec-requisitos',
            },
            InvalidUrlScheme: 'URL SAS inválida: la URL debe comenzar con https://.',
            InvalidUrlHost: 'URL SAS inválida: la URL debe apuntar a storageproveedores.blob.core.windows.net.',
            InvalidUrlSignature: 'URL SAS inválida: falta la firma de acceso (sig=). Copia la URL completa de Soriana.',
            InvalidUrlMissingId:
              'URL SAS inválida: falta el id de cuenta (ej. /portalproveedores/{id}). Copia la URL completa de Soriana.',
            SamePassword: 'La contraseña no cambió. Ingresa una nueva antes de guardar aquí.',
            SameToken: 'El token no cambió. Genera uno nuevo antes de guardar aquí.',
            NoChangesDetected:
              'No se puede actualizar las credenciales hasta que se actualicen el/los siguiente(s) campo(s):',
            AddCredential: 'Agregar Credencial',
            AddCredentialTitle: 'Agregar Nueva Credencial',
            SelectConnector: 'Selecciona un conector',
            CreateSuccess: 'La credencial ha sido creada correctamente',
            CredentialAlreadyExists: 'Ya existe una credencial para este conector',
            EmptyTitle: 'No hay cadenas conectadas',
            EmptyDescription: 'Aún no tenés conectores disponibles para tu cuenta.',
            SectionActive: 'Activas',
            SectionAvailable: 'Disponibles para conectar',
            SectionComingSoon: 'Próximamente',
            Card: {
              StatusConnected: 'Conectado',
              StatusAttention: 'Atención',
              StatusExpiring: 'Por expirar',
              StatusExpired: 'Expirada',
              StatusAvailable: 'Disponible',
              LastSync: 'Última actualización',
              User: 'Usuario',
              Edit: 'Editar',
              AvailableHint: 'Aún no la has conectado · disponible para usar',
              Connect: 'Conectar',
              Help: 'Ayuda',
              WarningExpired: 'Credenciales expiradas. Actualízalas para reanudar la sincronización.',
              WarningExpiring: 'Credenciales próximas a vencer. Actualiza tus credenciales.',
            },
            CountLabel_one: '{{count}} credencial',
            CountLabel_other: '{{count}} credenciales',
            EmptySearch: 'No se encontraron credenciales para "{{term}}"',
            SortDefault: 'Orden por defecto',
            SortByStatus: 'Ordenar por estado',
            SortByName: 'Ordenar por nombre',
            ViewCards: 'Tarjetas',
            ViewList: 'Lista',
            Search: 'Buscar credencial',
            List: {
              Retailer: 'Minorista',
              Status: 'Estado',
              User: 'Usuario',
              Updated: 'Actualizada',
              Actions: 'Acciones',
            },
            ComingSoon: {
              Badge: 'Próximamente',
              Notify: 'Avisar',
            },
            Placeholder: {
              Username: 'Usuario',
              Email: 'Correo electrónico',
              Password: 'Contraseña',
              Token: 'Token de acceso',
              Account: 'Cuenta',
              SasUrl: 'https://storageproveedores.blob.core.windows.net/...?sv=...&sig=...',
            },
            Form: {
              ConnectTitle: 'Conectar {{name}}',
              UpdateTitle: 'Actualizar credenciales de {{name}}',
              Cancel: 'Cancelar',
              Connect: 'Conectar',
              Update: 'Actualizar',
              ShowPassword: 'Mostrar contraseña',
              HidePassword: 'Ocultar contraseña',
              Description: {
                Default: 'Ingresa tus credenciales para conectar {{name}}.',
                Walmart: 'Ingresa tus credenciales de Retail Link para conectar Walmart.',
                Soriana: 'Ingresa la URL de acceso SAS proporcionada por Soriana para conectar tu cuenta.',
              },
            },
            Instructions: {
              NoteLabel: 'Nota',
              GuideLabel: 'Guía de integración',
              OpenGuide: 'Ver guía de configuración',
              Soriana: {
                Title: 'Cómo obtener tu URL SAS de Soriana',
                Steps: [
                  'Ingresa al Portal de Proveedores de Soriana en https://proveedores.soriana.com',
                  'Ve a la sección Configuración > Integraciones > Acceso a datos.',
                  'Localiza la URL SAS de tu cuenta de proveedor.',
                  'Copia la URL completa (incluye tu firma de acceso). Debe verse así: https://storageproveedores.blob.core.windows.net/portalproveedores/...?sv=...&sig=...',
                  'Pega la URL aquí en Bops para conectar tu cuenta.',
                ],
                Note: 'La URL SAS contiene una firma de acceso con tiempo limitado. Cuando expire, genera una nueva desde el portal de Soriana y actualízala aquí.',
              },
            },
          },
          CredentialIntake: {
            Title: 'Comparte tus credenciales',
            Description: 'Selecciona un conector y escribe tus credenciales para comenzar.',
            Loading: 'Cargando...',
            InvalidLink:
              'Este enlace no es válido o ha expirado. Contacta a tu representante de BOPS para obtener uno nuevo.',
            Submitted: 'Enviado',
          },
          ManualUpload: {
            Title: 'Carga manual',
            Uploading: 'Subiendo',
            Upload: 'Subir',
            All: 'Todas los archivos que puedes cargar',
            Loading: 'Por favor espera...\nEstamos recopilando los archivos que puedes cargar',
            Success: 'han sido subido(s) correctamente!',
            LoadingProgress: 'Cargando los archivos',
            Error: 'Ha ocurrido un error al subir el archivo, por favor intenta de nuevo.',
          },
          Integration: {
            All: 'Todas las integraciones',
            Loading: 'Por favor espera... Estamos recopilando tus integraciones',
            Title: 'Integraciones',
            Client: 'Cliente',
            Status: 'Estado',
            LastUpdated: 'Última actualización',
            File: 'Nombre del archivo',
            Late: 'Atrasado',
            OnTime: 'A tiempo',
            NextCron: 'Próxima fecha',
            Path: 'Ruta',
            Ignore: 'Ignorado',
            FileType: 'Tipo de archivo',
            Connector: 'Conector',
            SearchByPath: 'Buscar por ruta',
            SortNotAvailable: 'La opción de ordenar no está disponible para esta columna actualmente.',
            SelectClient: 'Seleccionar cliente(s)',
            SelectFileType: 'Seleccionar tipo(s) de archivo',
            SelectConnector: 'Seleccionar conector(es)',
          },
          User: {
            Password: 'Contraseña', // pragma: allowlist secret
            PasswordLengthError: 'La contraseña debe tener al menos 12 caracteres', // pragma: allowlist secret
            PasswordUppercaseError: 'La contraseña debe tener al menos una letra mayúscula', // pragma: allowlist secret
            PasswordLowercaseError: 'La contraseña debe tener al menos una letra minúscula', // pragma: allowlist secret
            PasswordNumberError: 'La contraseña debe tener al menos un número', // pragma: allowlist secret
            Title: 'Usuarios',
            ErrorEmail: 'Introduce un correo válido',
            UsernameNotEmail: 'El usuario no puede ser igual al correo',
            ErrorUsername:
              'El usuario sólo puede contener los siguientes caracteres alfanuméricos : "_" ,"+", "-", ".","!","#", "$", "^","~", "@", ","',
            BlockLabel: 'Bloquear',
            UnBlockLabel: 'Desbloquear',
            ErrorCreate: 'Error creando al usuario',
            ChangeAccount: 'Cambiar cliente',
            ErrorAlreadyExists: 'Error creando al usuario. El usuario ya existe.',
            ErrorChangeAccount: 'Error cambiando al cliente',
            ErrorBlock: 'Error bloqueando al user',
            Loading: 'Por favor espera...\nEstamos cargando tus usuarios',
            ConfirmBlock: '¿Estás seguro que quieres bloquear al usuario\n',
            ConfirmUnBlock: '¿Estás seguro que quieres desbloquear al usuario\n',
            ConfirmChangePassword: '¿Estás seguro que quieres enviar un correo para cambiar la contraseña de\n', // pragma: allowlist secret
            Select: 'Seleccionar un cliente',
            ErrorChangePassword: 'Error cambiando contraseña', // pragma: allowlist secret
            EmailSent: 'Correo enviado al usuario\n',
            ChangePassword: 'Enviar correo de cambiar contraseña', // pragma: allowlist secret
            LoadingEdit: 'El usuario se esta editando',
            Add: 'Añadir nuevo Usuario',
            LoadingChangePassword: 'Enviando correo para cambiar contraseña', // pragma: allowlist secret
            Never: 'Nunca',
            Edit: 'Editar',
            All: 'Todos los Usuarios',
            TitleModal: 'Nuevo Usuario',
            TitleEditModal: 'Editar Usuario',
            AddSuccess: 'se ha añadido correctamente',
            EditSuccess: 'Se ha editado correctamente',
            LoadingCreate: 'El usuario se esta creando',
            Client: 'Cliente',
            Tags: 'Tags',
            EditTags: 'Editar tags',
            EditTagsPlaceholder: 'Seleccione tags para el usuario',
            EditTagsEmpty: 'No hay tags disponibles',
            EditTagsLoadingError: 'Error cargando los tags disponibles',
            EditTagsError: 'Error actualizando los tags del usuario',
            EditPermissions: 'Editar permisos',
            EditPermissionsPlaceholder: 'Seleccione permisos para el usuario',
            EditPermissionsEmpty: 'No hay permisos disponibles',
            EditPermissionsLoadingError: 'Error cargando los permisos disponibles',
            EditPermissionsError: 'Error actualizando los permisos del usuario',
            ErrorSameClient: 'No se puede cambiar el usuario al mismo cliente',
            Status: 'Estado',
            Email: 'Correo',
            Username: 'Usuario',
            LastLogin: 'Último ingreso',
            Search: 'Buscar por correo',
            Blocked: 'Bloqueado',
            Active: 'Activo',
            UsernameLengthError: 'El usuario debe tener entre 6 y 30 caracteres',
            DeleteConfirm: '¿Estás seguro que quieres eliminar al usuario\n',
            ErrorDelete: 'Error eliminando al usuario',
            Delete: 'Eliminar',
            LoadingDelete: 'Eliminando usuario\n',
            ErrorInvalidUsername: 'Usuario inválido',
            ErrorUsernameTaken: 'Ese nombre de usuario ya está en uso. Elige uno diferente',
            UsernameCannotBeEmail: 'El usuario no puede ser un correo',
            UserModalLogoutMessage: '¿Estás seguro que quieres cerrar sesión?',
          },
          Supplier: {
            Loading: 'Por favor espera...\nEstamos cargando tus proveedores',
            Add: 'Añadir nuevo proveedor',
            Title: 'Administración de Proveedores',
            Exist: 'Proveedor ya existe',
            AddMessage: 'se ha añadido exitosamente',
            Progress: 'En Progreso',
            WithData: 'Data Disponible',
            WithoutData: 'Data Faltante',
            WithDataTooltip: 'Los datos de este proveedor están disponibles',
            WithoutDataTooltip:
              'Los datos de este proveedor no están disponibles. Por favor, solicitar credenciales para Walmart/Soriana',
            ProgressTooltip: ' La creación está en progreso. Los datos del proveedor estarán disponibles en 24 horas.',
            Status: 'Estado',
            Name: 'Nombre',
            Plan: 'Plan',
            Connectors: 'Credenciales',
            CreatedAt: 'Fecha de creación',
            Export: 'Exportar lista',
            ExportFilename: 'proveedores',
            Error: 'Error creando el proveedor',
            LoadingCreate: 'Creando el proveedor',
            All: 'Todos los Proveedores',
            TitleModal: 'Nuevo Proveedor',
            Search: 'Buscar por GLN o nombre',
            Pending: 'Pendiente por refrescar data',
            UserEmail: 'Correo del usuario',
            SelectPlan: 'Selecciona un plan',
            PlanRequired: 'El plan es obligatorio',
            PlanRequiresManualSetup: 'Requiere configuración manual — contacta a tu ejecutivo de cuenta.',
            SiebelLookupError:
              'No pudimos obtener los datos de este GLN — reintenta, o llena Nombre y Plan manualmente.',
            SiebelLookupPending: 'Buscando datos del GLN...',
            SiebelPrefillOverwrittenNameAndPlan:
              'El GLN cambió — Nombre y Plan se actualizaron con los datos de Siebel.',
            SiebelPrefillOverwrittenNameOnly:
              'El GLN cambió — Nombre se actualizó con los datos de Siebel. No se pudo determinar el Plan; selecciónalo manualmente.',
            AddonsColumn: 'Add-ons',
            AddonsCount: '{{count}} add-ons',
            ManageAddons: 'Administrar add-ons',
            UpdatePlan: 'Actualizar plan',
            ChangePlanTitle: 'Actualizar plan',
            ChangePlanWarning: 'Al actualizar el plan se eliminarán los {{count}} add-ons activos.',
            PlanChangeError: 'Error al actualizar el plan',
            PlanChangeSuccess: 'Plan actualizado exitosamente',
            PlanChangePending: 'Recibimos tu solicitud, pero este cambio requiere revisión manual antes de aplicarse.',
            AddonsModalTitle: 'Administrar add-ons',
            AddonsUpdateError: 'Error al actualizar los add-ons',
            AddonsUpdateSuccess: 'Add-ons actualizados exitosamente',
            NoAddonsAvailable: 'No hay add-ons disponibles para este plan',
            AddonsActive: 'Activos',
            AddonsAvailable: 'Disponibles',
            AddonsSelectedCount: '{{count}} add-ons seleccionados',
            RowActions: 'Acciones del proveedor',
            GenerateCredentialsLink: 'Generar link de credenciales',
            CredentialLinkCopied: 'Link copiado al portapapeles',
            CredentialLinkError: 'Error al generar el link',
            Save: 'Guardar',
            SaveChanges: 'Guardar cambios',
          },
          CredentialsSummary: {
            Title: 'Resumen de Credenciales',
            Loading: 'Por favor espera...\nEstamos cargando el resumen de credenciales',
            All: 'Todas las Credenciales',
            Search: 'Buscar GLN o nombre',
            FilterByConnector: 'Filtrar por conector',
            FilterByStatus: 'Filtrar por estado',
            Gln: 'GLN',
            ProviderName: 'Proveedor',
            Connector: 'Conector',
            Status: 'Estatus',
            DaysUntilExpiration: 'Días para expirar',
            LastUpdated: 'Última actualización',
            CreatedAt: 'Fecha de creación',
          },
          Mfa: {
            Title: 'Autenticación Multifactor',
            Phone: 'Teléfono',
            Loading: 'Por favor espera...\nEstamos cargando tus telefonos ',
            Message: 'Telefonos Registrados',
            Code: 'Obtener código',
            ErrorCode: 'Ha ocurrido un error al obtener el código',
            Close: 'Cerrar',
            TitleModal: 'Código',
            Description: 'Descripción',
          },
          MasterSourceSelect: {
            Column: 'Columna',
            NotSpecified: 'No especificado',
            SelectMasterSource: 'Seleccionar Fuente Maestra',
            ColumnName: 'Nombre de Columna',
            EnterColumnName: 'Ingresa el nombre de la columna',
            ColumnNameDescription: 'Especifica el nombre de la columna en los datos maestros que contiene los valores',
            Edit: 'Editar',
            Cancel: 'Cancelar',
            SaveChanges: 'Guardar Cambios',
          },
          Agent: {
            // UI Messages
            InputPlaceholder: 'Pregunta, busca o crea lo que necesites...',
            InputPlaceholderShort: 'Escribe tu pregunta...',
            OptionsHintWriteCustom: 'o escribe algo distinto',
            SendButton: 'Enviar',
            StopButton: 'Detener generación',
            AttachTooltip: 'Adjuntar archivo',
            RemoveAttachment: 'Quitar adjunto',
            CopyResponse: 'Copiar respuesta',
            Copied: 'Copiado',
            SaveTitle: 'Guardar',
            CancelSaveTitle: 'Cancelar',

            // Header Controls
            NewChat: 'Nuevo chat',
            SidebarMode: 'Modo barra lateral',
            FloatingMode: 'Modo flotante',
            Minimize: 'Minimizar',
            Maximize: 'Maximizar',
            CloseChat: 'Cerrar chat',
            HideSidebar: 'Ocultar barra lateral',

            // Thinking Indicator
            HideSteps: 'Ocultar pasos',
            ShowSteps: 'Ver {{count}} pasos del proceso',

            // Date Formatters
            Today: 'Hoy',
            Yesterday: 'Ayer',
            DaysAgo: 'Hace {{days}} días',
            Global: 'Global',

            // Sidebar indicators
            Processing: 'Procesando',
            NewResponse: 'Respuesta nueva',

            // Welcome Section
            WelcomeTitle: 'Asistente OSA',
            WelcomeDescription:
              'Analizo métricas, tendencias y te ayudo a optimizar el inventario basado en datos del módulo activo.',
            LoadingMessages: 'Cargando mensajes...',
            GuidedJourneys: 'Recorridos Guiados',
            LoadingJourneys: 'Cargando recorridos guiados...',
            LoadingAnalysis: 'Cargando tu análisis...',
            WelcomeGreeting: '¡Hola, {{name}}!',

            // Onboarding UI
            OnboardingPressEnter: 'Presiona Enter para continuar',
            OnboardingContinueWith: 'Continuar con {{count}} seleccionada{{plural}}',
            OnboardingCompletionTitle: 'Perfecto, {{name}}.',
            OnboardingCompletionSubtitle: 'Todo listo. Tu experiencia está configurada.',
            OnboardingCompletionMessage: 'Bienvenido a tu asistente inteligente de OSA.',
            OnboardingSkipHint: 'Presiona Esc para saltar',
            OnboardingSkipHintMobile: 'Toca para saltar',
            OnboardingSaveError: 'Error al guardar. Intenta de nuevo.',
            PreferencesBannerHighlight: 'Personaliza tu experiencia',
            PreferencesBannerText: 'con el asistente',
            PersonalizeSubtitle: 'Cuéntame un poco sobre ti.',
            GenericBrand: 'Asistente OSA',
            GreetingPersonal: 'Hola, {{name}}.',
            GreetingPrompt: '¿Por dónde quieres empezar hoy?',
            GreetingSubtitle:
              'Analizo métricas, tendencias y te ayudo a optimizar el inventario basado en datos del módulo activo.',
            GuidedJourneysLabel: 'Guided journeys',
            ComposerPlaceholder: 'Pregunta, busca o crea lo que necesites...',
            ComposerDisclaimer: 'El Agente OSA puede cometer errores. Confirma la información importante.',
            ConnectorListTitle: 'Cambiar retailer',
            ConversationsListTitle: 'Conversaciones',
            SearchPlaceholder: 'Buscar conversaciones...',
            GroupToday: 'Hoy',
            GroupYesterday: 'Ayer',
            GroupThisWeek: 'Esta semana',
            GroupOlder: 'Anteriores',
            EmptyConversationsTitle: 'Aún no hay conversaciones',
            EmptyConversationsDescription: 'Inicia una nueva para verla aquí.',
            EmptySearchTitle: 'Sin resultados',
            EmptySearchDescription: 'Nada coincide con "{{term}}".',
            EmptyJourneysTitle: 'Sin guided journeys',
            EmptyJourneysDescription: 'Los insights de este conector aparecerán aquí cuando estén listos.',
            EmptyTitle: 'Sin título',
            RenameAction: 'Editar nombre',
            DeleteAction: 'Eliminar',
            DeleteTitle: 'Eliminar conversación',
            DeleteDescription:
              'Esta acción no se puede deshacer. La conversación "{{title}}" y todas sus interacciones serán eliminadas.',
            Cancel: 'Cancelar',
            ThinkingSummary_one: 'Pensé durante {{seconds}}s · {{count}} paso',
            ThinkingSummary_other: 'Pensé durante {{seconds}}s · {{count}} pasos',
            ThinkingStepsCount_one: '{{count}} paso',
            ThinkingStepsCount_other: '{{count}} pasos',
            AriaResizeSidebar: 'Redimensionar barra del agente',
            AriaClearSearch: 'Limpiar búsqueda',
            AriaDisplayMode: 'Modo de visualización del agente',
            AriaOpenAgent: 'Abrir OSA Agent',
            AriaConversationActions: 'Acciones de conversación',
            AriaVoiceover: 'Dictar por voz',
            AriaShowThinkingLog: 'Mostrar log de pensamiento',
            AriaHideThinkingLog: 'Ocultar log de pensamiento',
            AriaScrollToBottom: 'Ir al último mensaje',
            AriaRegenerate: 'Regenerar',
            PreferencesModalTitle: 'Preferencias',
            PreferencesModalSubtitle: 'Edita cómo interactúo contigo',
            PreferencesSave: 'Guardar cambios',
            PreferencesClearAll: 'Limpiar todas las preferencias',
            PreferencesClearConfirm: '¿Estás seguro? Esto reiniciará tu personalización.',
            PreferencesClearConfirmAction: 'Eliminar',
            PreferencesClearCancel: 'Cancelar',
            PreferencesSaving: 'Guardando...',
            PreferencesDeleting: 'Eliminando...',
            EditPreferences: 'Editar preferencias',

            // Error Messages (UI level)
            ErrorUnexpected: 'Ocurrió un error inesperado.',
            ErrorTimeout: 'La solicitud tardó demasiado tiempo. Por favor, intenta de nuevo.',
            ErrorNetwork: 'Error de conexión. Verifica tu conexión a internet.',
            ErrorJourneyUnavailable:
              'No se pudieron cargar las instrucciones de este guided journey, así que no se envió nada al agente ni se generó ningún reporte. Por favor, intenta de nuevo en un momento.',
            ErrorAgentPrefix: 'Error al contactar el agente:',
            ErrorNoResponse: 'No se recibió respuesta del agente.',
            ErrorAgentUnavailable: 'Disculpe, el agente no está disponible en este momento.',
            ErrorGenerationStopped: 'Generación detenida por el usuario.',
            FileDownloadError: 'Error al descargar el archivo. Por favor, intenta de nuevo.',
            ImageExpand: 'Ampliar imagen',
            ImageDownload: 'Descargar imagen',
            ImageZoomIn: 'Acercar',
            ImageZoomOut: 'Alejar',
            ImageZoomReset: 'Restablecer zoom',
            ImageClose: 'Cerrar',

            // Thinking Messages
            ThinkingInitial: 'Iniciando análisis…',

            // Suggestions
            SuggestionTopOpportunitiesDisplay: 'Precisión de Pronósticos por Categoría',
            SuggestionTopOpportunitiesPrompt:
              '¿Dónde están fallando mis pronósticos de demanda y cómo puedo mejorar su precisión por categoría?',
            SuggestionBiggestRisksDisplay: 'Productos en Riesgo de Agotarse',
            SuggestionBiggestRisksPrompt:
              '¿Qué productos están en mayor riesgo de agotarse y cuál es el impacto en ventas perdidas?',
            SuggestionSeasonalAdjustDisplay: 'Reducir Exceso de Inventario',
            SuggestionSeasonalAdjustPrompt:
              '¿Qué productos tienen exceso de inventario y qué estrategias puedo aplicar para reducirlo?',
            SuggestionPerformanceSummaryDisplay: 'Mejorar Disponibilidad en Anaquel',
            SuggestionPerformanceSummaryPrompt: '¿Por qué tengo baja disponibilidad en anaquel y cómo puedo mejorarla?',

            // Browser Notifications
            NotificationBannerText:
              'Esto puede tardar un momento. Recibe una notificación cuando la respuesta esté lista.',
            NotificationEnable: 'Activar',
            NotificationDismiss: 'Cerrar',
            NotificationTitle: 'Agente OSA',
            NotificationBody: 'Tu análisis está listo. Haz clic para ver los resultados.',
            NotificationWaiting: 'Permite las notificaciones en el aviso del navegador de arriba.',
            NotificationEnabled: 'Notificaciones activadas. Te avisaremos si sales de esta pestaña.',
            NotificationDenied: 'Notificaciones bloqueadas. Habilítalas en la configuración de tu navegador.',

            // Disclaimer
            Disclaimer: 'El Agente OSA puede cometer errores. Confirma la información importante.',

            // Feedback Modal
            FeedbackTitle: 'Feedback',
            FeedbackSubmit: 'Enviar',
            FeedbackCancel: 'Cancelar',
            FeedbackQuestion: '¿Te sirvió esta respuesta?',
            FeedbackHelpful: 'Útil',
            FeedbackNotHelpful: 'No fue útil',
            FeedbackWhatWentWrong: '¿Qué salió mal? (opcional)',
            FeedbackExpectedPlaceholder: '¿Qué esperabas ver? (opcional)',
            FeedbackDisclosure:
              'El equipo de BOPS revisa este feedback junto con la respuesta. No se usa para entrenar modelos.',
            FeedbackReasonWrongNumbers: 'Números incorrectos',
            FeedbackReasonWrongPeriodOrFilter: 'Periodo o filtro equivocado',
            FeedbackReasonMisunderstoodQuestion: 'No entendió mi pregunta',
            FeedbackReasonMissingDataOrChart: 'Falta información o gráfico',
            FeedbackReasonTooLongOrConfusing: 'Demasiado largo o confuso',
            FeedbackReasonTooSlowOrError: 'Muy lento o con error',
            FeedbackReasonOther: 'Otro',
            FeedbackPositiveTitle: 'Nos alegra que te sirviera',
            FeedbackWhatWorkedWell: '¿Qué te resultó útil? (opcional)',
            FeedbackLikedPlaceholder: '¿Algo más que quieras contarnos? (opcional)',
            FeedbackReasonAccurateData: 'Datos correctos',
            FeedbackReasonClearAnswer: 'Respuesta clara',
            FeedbackReasonSavedTime: 'Me ahorró tiempo',
            FeedbackReasonGoodInsight: 'Buen análisis',
          },
          ConfigurationPolicy: {
            TargetInventory: {
              CalculationType: 'Tipo de Cálculo',
              CalculationTypeDesc: 'Selecciona cómo deben calcularse los valores de inventario objetivo',
              BopsOptimalInventory: 'Inventario Óptimo Bops',
              BopsOptimalDesc: 'Cálculo automatizado basado en el nivel de servicio',
              Provided: 'Proporcionado',
              ProvidedDesc: 'Usar valores de datos fuente',
              DefaultCalculationType: 'Tipo de Cálculo Predeterminado',
              Source: 'Fuente',
              SourceDesc: 'Fuente del objetivo proporcionado',
              User: 'Usuario',
              UserTooltip: 'Usar entradas internas para establecer objetivos de inventario',
              Customer: 'Cliente',
              CustomerTooltip:
                'Usar valores proporcionados por el cliente para objetivos de inventario (ej. minorista)',
              CustomerMessage:
                'Se utilizará la configuración de inventario proporcionada por el cliente. No se requieren configuraciones adicionales.',
              CustomerMessageWalmart:
                'Inventario Objetivo: Número máximo de unidades que pueden mantenerse en el anaquel del piso de ventas. Los valores se obtendrán de los sistemas de Walmart.',
              CustomerMessageTarget:
                'Inventario Objetivo: Cantidad Mínima de Presentación Final - cantidad mínima de inventario que debe estar disponible en tienda para mantener la disponibilidad operacional. Los valores se obtendrán de los sistemas de Target.',
              CustomerMessageFarmatodo:
                'Inventario Objetivo: Cantidad Máxima de Anaquel - cantidad máxima permitida de inventario para un producto en una ubicación para prevenir exceso de stock. Los valores se obtendrán de los sistemas de Farmatodo.',
              ConfigurationType: 'Tipo de Configuración',
              ConfigurationTypeDesc: 'Tipo de configuración para el objetivo proporcionado',
              Units: 'Unidades',
              UnitsTooltip: 'Mostrar objetivos de inventario como un conteo de unidades',
              DaysOnHand: 'Días de Inventario',
              DaysOnHandTooltip: 'Mostrar objetivos de inventario como el número de días de suministro',
              UnitsType: 'Tipo de Unidades',
              UnitsTypeDesc: 'Tipo de unidades para el objetivo proporcionado',
              Target: 'Objetivo',
              TargetDesc: 'Nivel de inventario objetivo',
              TargetTooltip: 'Establecer niveles de inventario objetivo',
              ReorderPoint: 'Punto de Reorden',
              ReorderPointDesc: 'Nivel de inventario del punto de reorden',
              ReorderPointTooltip: 'Establecer niveles de punto de reorden',
              SafetyStock: 'Stock de Seguridad',
              SafetyStockDesc: 'Nivel de inventario de stock de seguridad',
              SafetyStockTooltip: 'Establecer niveles de stock de seguridad',
              DaysOnHandDirection: 'Dirección de Días de Inventario',
              DaysOnHandDirectionDesc: 'Dirección de días de inventario para el objetivo proporcionado',
              Backward: 'Pasado',
              BackwardDesc: 'Considerar días pasados de inventario (Venta)',
              Forward: 'Futuro',
              ForwardDesc: 'Proyectar días futuros de inventario (Pronóstico)',
              DataSource: 'Fuente de Datos',
              DataSourceDesc: 'Fuente de datos para el objetivo proporcionado',
              DefaultValue: 'Valor Predeterminado ({{type}})',
              Days: 'Días',
              ServiceLevel: 'Nivel de Servicio',
              ServiceLevelDesc: 'Especificar el nivel de servicio objetivo (0-100%)',
              ServiceLevelPercent: 'Nivel de Servicio (%)',
            },
            Demand: {
              DemandIndicator: 'Indicador de Demanda',
              DemandIndicatorDesc: 'Tipo de datos de demanda a utilizar para los cálculos',
              SellOut: 'Venta Final',
              SellOutTooltip: 'Ventas directas a consumidores finales',
              SellIn: 'Venta a Canal',
              SellInTooltip: 'Ventas a minoristas o distribuidores',
              Forecast: 'Pronóstico',
              ForecastTooltip: 'Demanda futura proyectada',
              DemandWindow: 'Ventana de Demanda',
              DemandWindowDesc: 'Período de tiempo a considerar para los cálculos de demanda',
              FourWeeks: '4 Semanas',
              EightWeeks: '8 Semanas',
              TwelveWeeks: '12 Semanas',
              TwentySixWeeks: '26 Semanas',
              FiftyTwoWeeks: '52 Semanas',
              OneHundredFourWeeks: '104 Semanas',
              OneMonth: '1 mes',
              TwoMonths: '2 meses',
              ThreeMonths: '3 meses',
              SixMonths: '6 meses',
              OneYear: '1 año',
              TwoYears: '2 años',
              AdvancedSettings: 'Configuraciones Avanzadas',
              DemandVariability: 'Variabilidad de Demanda',
              DemandVariabilityDesc:
                'Factor adicional de incertidumbre de demanda para considerar fluctuaciones del mercado y precisión de pronósticos',
              DemandVariabilityPercent: 'Variabilidad de Demanda (%)',
            },
            Supply: {
              LeadTimes: 'Tiempos de Entrega',
              LeadTimesDesc: 'Tiempos de entrega para la cadena de suministro',
              DefaultLeadTimeDays: 'Tiempo de Entrega Predeterminado (Días)',
              AdvancedSettings: 'Configuraciones Avanzadas',
              LeadTimesVariability: 'Variabilidad de Tiempos de Entrega',
              LeadTimesVariabilityDays: 'Variabilidad de Tiempos de Entrega (Días)',
              ReorderRate: 'Tasa de Reorden',
              OrderFrequency: 'Frecuencia de Pedido',
              OrderFrequencyDesc: 'Configurar parámetros de frecuencia para patrones y ciclos de pedidos de suministro',
              DefaultValue: '{{type}} Predeterminado (Días)',
            },
            Inventory: {
              InventoryOnHand: 'Inventario en Piso',
              InventoryOnHandDesc: 'Incluir inventario actual disponible en los cálculos',
              AdditionalInventoryOptions: 'Opciones Adicionales de Inventario',
              AdditionalInventoryOptionsDesc: 'Incluir opciones adicionales de inventario en los cálculos',
              OnOrder: 'En Pedido',
              OnOrderTooltip: 'Pedidos realizados a proveedores pero aún no recibidos',
              InTransit: 'En Tránsito',
              InTransitTooltip: 'Inventario en tránsito entre ubicaciones',
              InWarehouse: 'En Almacén',
              InWarehouseTooltip: 'Inventario en centros de distribución utilizado para reabastecer el stock en tienda',
              Allocated: 'Asignado',
              AllocatedTooltip: 'Pedidos de clientes que han sido confirmados pero aún no cumplidos',
              Aging: 'Vencimiento',
              AgingTooltip: 'Inventario que no puede venderse debido a su frescura',
              Custom: 'Personalizado',
              CustomTooltip: 'Configuración de inventario personalizada',
              ImpactIncrease: 'Aumenta el inventario disponible',
              ImpactDecrease: 'Disminuye el inventario disponible',
              ImpactBoth: 'Puede aumentar o disminuir el inventario disponible',
            },
            CPFR: {
              Title: 'Configuración Smart CPFR',
              TargetInventory: 'Inventario Objetivo',
              Demand: 'Demanda',
              Supply: 'Suministro',
              InventoryAvailable: 'Inventario Disponible',
              UnconfiguredDisclaimerTitle: 'Smart CPFR No Configurado',
              UnconfiguredDisclaimerMessage:
                'Las recomendaciones de Bops pueden ser inexactas debido a la falta de configuración. Por favor contacta a tu Operador de Cuenta para completar la configuración de Smart CPFR.',
            },
            OSA: {
              Title: 'Configuración de Disponibilidad en Anaquel',
              OsaCalculation: 'Cálculo OSA',
              AuditAlerts: 'Alertas de Auditoría',
              BopsRecommended: 'Recomendado por Bops',
              BopsRecommendedDesc: 'Cálculo automático basado en datos históricos de ventas',
              Provided: 'Proporcionado',
              ProvidedDesc: 'Utilizar valores de archivos maestros',
              Passive: 'Pasivo',
              Average: 'Promedio',
              Aggressive: 'Agresivo',
              AllAlerts: 'Todas las Alertas',
              AllAlertsDesc: 'Seleccionar todas las alertas definidas por el cálculo OSA',
              AllAlertsInScope: 'Todas las Alertas en Alcance',
              TopOpportunityAlerts: 'Mejores Oportunidades (Ventas Perdidas)',
              TopSellerAlerts: 'Productos Más Vendidos',
              Daily: 'Diario',
              Weekly: 'Semanal',
              Custom: 'Personalizado',
              MerchandiserIntegration: 'Integración con Merchandiser',
              FieldOpsIntegration: 'Integración con Operaciones de Campo',
              WhatsAppChatbot: 'Chatbot de WhatsApp',
              AuditAlertScope: 'Alcance de Alertas de Auditoría',
              AuditAlertScopeDesc: 'Selecciona qué productos habilitar para auditorías',
              AuditAlertPrioritization: 'Priorización de Alertas de Auditoría',
              AuditAlertPrioritizationDesc: 'Selecciona qué productos deben priorizarse para auditorías',
              AuditAlertFrequency: 'Frecuencia de Alertas de Auditoría',
              AuditAlertFrequencyDesc: 'Con qué frecuencia deben realizarse las auditorías',
              AuditAlertDelivery: 'Entrega de Alertas de Auditoría',
              AuditAlertDeliveryDesc: 'Selecciona cómo deben entregarse las alertas de auditoría',
              DaysSinceLastSalesThreshold: 'Días Desde Última Venta',
              DaysSinceLastSalesThresholdDesc: 'Número de días desde la última venta para activar una alerta',
              DefaultValueDays: 'Valor Predeterminado (Días)',
              SalesSensitivityProbability: 'Probabilidad de Sensibilidad de Ventas',
              SalesSensitivityProbabilityDesc: 'Configura la probabilidad de sensibilidad de ventas',
            },
            LockIconTooltip: 'Para editar la configuración. Por favor contacta a tu Operador de Cuenta de Bops',
          },
          SupplierOnboarding: {
            Badge: 'Configurando tu panel',
            WelcomeWithName: 'Bienvenido, {{name}}.',
            WelcomeGeneric: 'Bienvenido a Bops.',
            Description: 'Estamos procesando tus datos. Tu panel estará listo en menos de',
            DescriptionTimeframe: '24 horas',
            EmailWhenReady: 'Te enviaremos un correo apenas tu panel esté listo.',
            NotifyMe: 'Avísame cuando esté listo',
            ViewTraining: 'Ver capacitación',
            PreviewLabel: 'Vista previa de tu panel',
            PanelIncludes: 'Lo que incluye tu panel',
            Steps: {
              ConnectedLabel: 'Fuentes de datos conectadas',
              ConnectedDetail: 'Credenciales verificadas',
              ProcessingLabel: 'Procesando datos',
              ProcessingDetail: 'Ventas, inventario y distribución',
              ReadyLabel: 'Panel listo',
              ReadyDetail: 'KPIs, alertas y reportes disponibles',
            },
            Features: {
              DashboardLabel: 'Panel',
              DashboardDescription: 'KPIs de ventas, inventario y disponibilidad de un vistazo',
              AlertsLabel: 'Alertas',
              AlertsDescription: 'Indicadores de tendencia que detectan problemas antes de que ocurran',
              ChainSummaryLabel: 'Resumen por cadena',
              ChainSummaryDescription: 'Análisis interactivo por cadena y producto con desglose',
              ExportLabel: 'Exportar datos',
              ExportDescription: 'Descarga CSV a nivel tienda-producto-semana para Excel',
            },
          },
          FieldService: {
            Title: 'Servicios de Campo',
            Loading: 'Un momento, estamos reuniendo tus servicios de campo...',
            LoadingDetail: 'Un momento, estamos reuniendo este servicio de campo...',
            BackToList: 'Servicios de Campo',
            ModeViewer: 'Ver',
            ModeEdit: 'Editar',
            ModeAriaLabel: 'Modo de visualización',
            NotConfigured: 'Sin configurar',
            List: {
              FieldService: 'Servicio de campo',
              Stores: 'Tiendas cubiertas',
              StoresCount_one: '{{count}} tienda',
              StoresCount_other: '{{count}} tiendas',
              TaskTypes: 'Tipos de Tarea',
              EmptyTitle: 'Aún no hay servicios de campo',
              EmptyDescription: 'Los servicios de campo contratados por este cliente aparecerán aquí.',
            },
            TaskTypeName: {
              pog: 'Auditoría de Planograma',
              price: 'Auditoría de Precio',
              shelf: 'Auditoría y Ejecución de Anaquel',
              promoae: 'Auditoría y Ejecución de Promoción/Exhibición',
              promo: 'Despliegue de Promoción/Exhibición',
            },
            TaskTypeShort: {
              pog: 'Planograma',
              price: 'Precio',
              shelf: 'Anaquel',
              promoae: 'Auditoría Promo',
              promo: 'Promo/Exhibición',
            },
            Sections: {
              Coverage: 'Cobertura',
              Availability: 'Disponibilidad',
              AvailabilityDesc: 'Con qué frecuencia este servicio de campo puede atender las tiendas cubiertas.',
              TaskTypes: 'Tipos de Tarea',
              Workers: 'Trabajadores',
            },
            Contract: {
              TaskVolumeDesc: 'Cantidad objetivo de tareas por bloque de visita.',
              TasksPerVisit: 'Tareas por visita',
              VisitsPerPeriod: 'Visitas por período',
              ApprovedTaskTypesDesc: 'Trabajo que este servicio de campo está aprobado para realizar.',
            },
            ContractStatus: {
              active: 'Activo',
              ended: 'Finalizado',
            },
            Availability: {
              Daily: 'Diario',
              DailySub: 'Todos los días hábiles',
              Weekly: 'Semanal',
              Monthly: 'Mensual',
              WeeklyHeadline_one: 'Hasta {{count}} visita por semana',
              WeeklyHeadline_other: 'Hasta {{count}} visitas por semana',
              MonthlyHeadline_one: 'Hasta {{count}} visita por mes',
              MonthlyHeadline_other: 'Hasta {{count}} visitas por mes',
              Sub: 'En tiendas cubiertas',
              LeadTime_one: '{{count}} día de anticipación',
              LeadTime_other: '{{count}} días de anticipación',
            },
            Workers: {
              Description: 'Miembros del equipo de campo empleados por este servicio de campo.',
              EmptyTitle: 'Aún no hay trabajadores',
              EmptyDescription: 'Este servicio de campo todavía no tiene trabajadores en su roster.',
            },
            WorkerStatus: {
              on: 'Activo',
              off: 'Inactivo',
              idle: 'En espera',
            },
            Coverage: {
              Description:
                'Los contratos son por cadena. Una cadena está disponible cuando al menos una de sus tiendas está cubierta.',
              TotalStores_one: '{{count}} tienda',
              TotalStores_other: '{{count}} tiendas',
              StoresCovered_one: '{{count}} cubierta',
              StoresCovered_other: '{{count}} cubiertas',
              Available: 'Disponible',
              Unavailable: 'No disponible',
              ViewStores: 'Ver tiendas',
              ModalTitlePrefix: 'Tiendas',
              ModalSubtitle: 'Tiendas que cubre este servicio de campo.',
              SearchPlaceholder: 'Buscar tiendas por nombre…',
              NoMatches: 'Ninguna tienda coincide con esta búsqueda.',
              LoadError: 'No se pudieron cargar las tiendas.',
              ModalCount: '{{covered}} cubiertas · mostrando {{matched, number}} de {{total, number}}',
              PagerAriaLabel: 'Paginación de tiendas',
              PreviousPage: 'Página anterior',
              NextPage: 'Página siguiente',
              CloseModal: 'Cerrar',
            },
          },
          Schedule: {
            Title: 'Cronogramas',
            Loading: 'Un momento, estamos reuniendo tus cronogramas...',
            SearchPlaceholder: 'Buscar cronogramas por nombre…',
            Empty: {
              Title: 'Aún no hay cronogramas',
              Description: 'Los cronogramas que crees aparecerán aquí.',
              NoResultsTitle: 'Ningún cronograma coincide',
              NoResultsDescription: 'Prueba con otra búsqueda o quita los filtros.',
            },
            CreateButton: 'Crear cronograma',
            CreateNoPermission: 'No tienes permiso para crear cronogramas.',
            RefreshError: 'No se pudo actualizar la página. Recárgala para ver los últimos cambios.',
            Wizard: {
              Title: 'Armar un cronograma',
              BackToList: 'Cronogramas',
              Back: 'Atrás',
              Next: 'Siguiente',
              Finish: 'Crear cronograma',
              CreateSuccess: 'Cronograma creado',
              CreateError: 'No se pudo crear el cronograma. Intenta de nuevo.',
              AlertsOnlyScheduleName: 'Cronograma solo de alertas',
              PrioritizeByWeekScheduleName: 'Cronograma de prioridades semanales',
              NewScheduleName: 'Nuevo cronograma',
              EligibleFieldServices_one: '{{count}} servicio de campo elegible',
              EligibleFieldServices_other: '{{count}} servicios de campo elegibles',
              Steps: {
                where: 'Dónde',
                what: 'Qué',
                frequency: 'Frecuencia',
                firm: 'Servicio de campo',
              },
              Where: {
                RetailersLabel: 'Cadenas',
                AllRetailers: 'Todas las cadenas',
                SectionSubtitle:
                  'Selecciona una cadena completa o una de sus regiones a la izquierda, o elige tiendas individuales.',
                OneRetailerHint: 'Una cadena por cronograma',
                StoreSearchPlaceholder: 'Buscar tiendas por nombre o región…',
                SelectAll: 'Seleccionar las {{count}}',
                ClearAll: 'Deseleccionar estas',
                StoresSelected_one: '{{count}} seleccionada',
                StoresSelected_other: '{{count}} seleccionadas',
                NoStores: 'Ninguna tienda coincide con este filtro.',
                ColumnStore: 'Tienda',
                ColumnRegion: 'Región',
                ShowingCount: 'Mostrando {{shown}} de {{total}} tiendas',
              },
              What: {
                SectionSubtitle: 'Dos decisiones: qué dispara el trabajo y cuál es el trabajo.',
                WorkMixLabel: 'Qué tipo de trabajo',
                WorkMixAlertsOnly: 'Solo alertas',
                WorkMixAlertsOnlyDesc: 'Las alertas en vivo llenan toda la visita.',
                WorkMixScheduleOnly: 'Solo cronograma',
                WorkMixScheduleOnlyDesc: 'Solo tareas programadas, sin alertas.',
                WorkMixBoth: 'Alertas y cronograma',
                WorkMixBothDesc: 'Las alertas toman una parte limitada; las tareas programadas toman el resto.',
                AlertsTitle: 'Alertas',
                AlertsExplainAlertsOnly:
                  'Sin cronograma asociado. Cada visita abre todo su tiempo a las alertas en vivo, ordenadas por valor recuperado.',
                AlertsExplainBoth:
                  'Reserva una parte limitada del tiempo de visita para alertas en vivo de quiebre de stock, stock bajo, falta de exhibición y desvío de planograma.',
                AlertCeilingDefaultLabel: 'Techo por defecto: {{pct}}% de la visita',
                AlertCeilingDefaultNote:
                  'Las tareas programadas toman el {{remaining}}% restante · se puede cambiar por semana',
                ScheduledTasksLabel: 'Tareas programadas',
                CategoriesLabel: 'Categorías',
                NoCategories: 'No se encontraron categorías para los productos de esta cadena.',
                MissingCategoriesWarning:
                  'Elige al menos una categoría para que estos tipos de tarea tengan productos que auditar: {{taskTypes}}',
                TaskTypesSelected_one: '{{count}} tipo de tarea seleccionado',
                TaskTypesSelected_other: '{{count}} tipos de tarea seleccionados',
                TaskTypesNoneSelected: 'Ningún tipo seleccionado',
                DecideWeeklyLabel: 'Priorizar semana a semana',
                DecideWeeklyDesc: 'En vez de fijar la lista ahora, elegir las tareas cada semana',
                DecideWeeklyChosenEachWeek: 'Se elige cada semana',
                DecideWeeklyNoteScheduleOnly:
                  'Sin lista fija de tareas. Eliges las tareas de cada visita semana a semana. Puedes editar el cronograma en cualquier momento para fijar una lista.',
                DecideWeeklyNoteBoth:
                  'Todavía no hay tareas programadas. Las alertas llenan cada visita hasta que agregues algunas. Puedes editar el cronograma en cualquier momento para sumarlas.',
              },
              Frequency: {
                SectionSubtitle: '¿Con qué frecuencia deben repetirse estas visitas?',
                CadenceOnce: 'Una vez',
                CadenceOnceDesc: 'Una sola visita en la fecha que elijas',
                CadenceWeekly: 'Semanal',
                CadenceWeeklyDesc: 'Se repite en los días de la semana que elijas',
                CadenceBiWeekly: 'Quincenal',
                CadenceBiWeeklyDesc: 'Se repite en los días que elijas, cada dos semanas',
                CadenceMonthly: 'Mensual',
                CadenceMonthlyDesc: 'Se repite en los días elegidos de cada mes',
                WeekdaysLabel: 'En estos días',
                MonthlyRepeatsOnLabel: 'Se repite en',
                MonthlyOrdinalPrefix: 'El',
                MonthlyOrdinalSuffix: 'del mes',
                MonthlyOrdinalFirst: 'primer',
                MonthlyOrdinalSecond: 'segundo',
                MonthlyOrdinalThird: 'tercer',
                MonthlyOrdinalFourth: 'cuarto',
                MonthlyOrdinalLast: 'último',
                MonthlyOrdinalNote: 'Se ejecuta el {{ordinalLabel}} {{weekday}} de cada mes',
                VisitDateLabel: 'Fecha de la visita',
                VisitDateNote: 'Se generará una sola visita en esta fecha.',
                StartDateLabel: 'Fecha de inicio',
                EndDateLabel: 'Fin',
                EndDateNone: 'Sin fecha de fin',
                EndDatePreset: 'Termina después de',
                EndDateCustom: 'Termina en una fecha específica',
                WeeksSuffix_one: '{{count}} semana',
                WeeksSuffix_other: '{{count}} semanas',
              },
              Firm: {
                Title: 'Servicio de campo',
                SectionSubtitle:
                  'Elige quién ejecuta las visitas. Se muestran los servicios de campo con un contrato activo para las cadenas que seleccionaste.',
                NameLabel: 'Nombre del cronograma',
                NameRequired: 'Ponle un nombre al cronograma para crearlo.',
                ModeSwitcherAriaLabel: 'Modo de asignación de servicio de campo',
                ModeSingle: 'Un servicio de campo',
                ModeSplit: 'Dividir',
                LoadingEligible: 'Verificando qué servicios de campo son elegibles…',
                NoEligible:
                  'Ningún servicio de campo tiene un contrato activo para estas cadenas todavía. Vuelve atrás y ajusta las tiendas, o agrega un nuevo servicio de campo.',
                NoSharedTaskType:
                  'Ningún tipo de tarea está aprobado por todos los servicios de campo seleccionados, así que este cronograma nunca podría tener objetivos. Elige servicios de campo que compartan al menos un tipo de tarea aprobado.',
                EligibilityError:
                  'No pudimos verificar la elegibilidad de los servicios de campo. Cambia una selección para reintentar, o vuelve más tarde.',
                AssignPlaceholder: 'Asignar servicio de campo…',
                NoCoveragePlaceholder: 'Sin cobertura',
                PartialCoverageBanner: 'Cubre {{covered}} de {{total}} tiendas solicitadas. Sin cobertura: {{stores}}',
                PartialCoverageMoreStores: '{{stores}} y {{count}} más',
                ClearAssignment: 'Quitar asignación',
                SplitShowingRange: 'Mostrando {{from}}–{{to}} de {{total}} tiendas',
                SplitPagerAriaLabel: 'Paginación de asignación de tiendas',
                PreviousPage: 'Página anterior',
                NextPage: 'Página siguiente',
              },
            },
            Filters: {
              Status: 'Estado',
              FieldService: 'Servicio de Campo',
              AllFieldServices: 'Todos los servicios de campo',
            },
            StatusFilter: {
              all: 'Todos',
              active: 'Activo',
              paused: 'Pausado',
              ended: 'Finalizado',
            },
            ViewToggle: {
              AriaLabel: 'Vista de cronogramas',
              Table: 'Vista de tabla',
              Cards: 'Vista de tarjetas',
            },
            Card: {
              Stores: 'Tiendas',
              StoresCount_one: '{{count}} tienda',
              StoresCount_other: '{{count}} tiendas',
            },
            Delete: {
              Action: 'Eliminar cronograma',
              Confirm:
                '¿Eliminar el cronograma "{{name}}"? También se eliminan sus objetivos y semanas. Esta acción no se puede deshacer.',
              Error: 'No se pudo eliminar el cronograma. Intenta de nuevo.',
            },
            List: {
              RowActions: 'Acciones de {{name}}',
              FieldServices: 'Servicios de Campo',
              Name: 'Cronograma',
              Scope: 'Alcance',
              Cadence: 'Frecuencia',
              Status: 'Estado',
            },
            RecurrenceLabel: '{{cadence}} · {{recurrence}}',
            MonthlyRecurrence: '{{ordinal}} {{weekday}}',
            CadenceLabel: {
              once: 'Una vez',
              weekly: 'Semanal',
              bi_weekly: 'Quincenal',
              monthly: 'Mensual',
            },
            Status: {
              active: 'Activo',
              paused: 'Pausado',
              ended: 'Finalizado',
            },
            Configuration: {
              Eyebrow: 'Configuración',
              BackToSchedule: 'Volver al cronograma',
              ExcelSource: 'Carga de Excel',
              Settings: 'Ajustes',
              Pause: 'Pausar',
              Resume: 'Reanudar',
              End: 'Terminar cronograma',
              EndConfirm:
                'Terminar {{name}} detiene todas las visitas futuras y lo deja de solo lectura. No se puede deshacer. ¿Deseas continuar?',
              PausedToast: 'Pausaste {{name}}',
              ResumedToast: 'Reanudaste {{name}}',
              EndedToast: 'Terminaste {{name}}',
              StatusError: 'No pudimos cambiar el estado del cronograma',
              Tiles: {
                Stores: 'Tiendas',
                TaskTypes: 'Tipos de tarea',
                Cadence: 'Frecuencia',
                FieldServices: 'Servicios de campo',
              },
              Name: {
                Label: 'Nombre del cronograma',
                Help: 'Se muestra en la lista de Cronogramas y al equipo de campo',
              },
              Cadence: {
                Label: 'Frecuencia',
                Dates: 'Del {{start}} al {{end}}',
                NoEnd: 'sin fecha de fin',
                Edit: 'Cambiar',
                Saved: 'Frecuencia actualizada',
                SaveError: 'No pudimos guardar la frecuencia',
                LoadError: 'No pudimos cargar el calendario de visitas',
                NotEditableLegacy: 'Esta frecuencia se guardó en un formato anterior y no se puede editar desde aquí',
                LegendVisit: 'Visita programada',
                LegendToday: 'Hoy',
              },
              Upcoming: {
                Title: 'Próximas visitas',
                PlannedVisits: 'visitas planeadas en las próximas {{weeks}} semanas',
                Next: 'Próxima visita: {{date}}',
                None: 'Sin visitas programadas en esta ventana',
              },
              TaskTypes: {
                Title: 'Tipos de tarea',
                Empty: 'Todavía no hay tipos de tarea configurados',
                LoadError: 'No pudimos cargar los tipos de tarea',
              },
              Ceiling: {
                Label: 'Techo de alertas',
                NotApplicable: 'No aplica',
                Edit: 'Cambiar',
                Save: 'Guardar',
                Cancel: 'Cancelar',
                CurrentWeekKeptHint:
                  'La semana en curso conserva el techo que tiene ahora; el cambio aplica desde la semana siguiente.',
                SwitchConfirm: 'Se restablecerán los techos de las semanas futuras. ¿Deseas continuar?',
                SaveError: 'No pudimos guardar el techo de alertas',
              },
              CeilingNote: {
                hybrid:
                  'Las alertas ocupan hasta esta porción de la visita. Los objetivos programados toman lo que quede, nunca por debajo del mínimo.',
                alerts_only: 'Solo alertas. Sin trabajo proactivo que proteger, toda la visita va a alertas.',
                scheduled_only: 'Solo trabajo programado. Ninguna alerta alimenta este cronograma.',
              },
              Conflict: {
                schedule_ended: 'Este cronograma ya terminó. Solo se puede cambiar su nombre.',
                ended_status_change: 'Un cronograma terminado no puede cambiar de estado.',
                not_started: 'Este cronograma aún no comienza. Elimínalo en lugar de terminarlo.',
                started_weeks_changed:
                  'Este cambio eliminaría semanas que ya comenzaron. Termina este cronograma y crea uno nuevo.',
                schedule_expired: 'Este cronograma ya venció. Crea uno nuevo.',
                past_visit_added: 'Este cambio agregaría una visita en una fecha que ya pasó.',
                composition_published:
                  'Este cronograma tiene objetivos publicados, así que su tipo ya no puede cambiar.',
                published_weeks_dropped:
                  'Una semana con objetivos publicados dejaría de ser semana de visita. Mantenla en la frecuencia.',
                published_objectives: 'Este cronograma tiene objetivos publicados, así que ya no puede cambiar.',
                no_objectives:
                  'Un cronograma solo programado necesita al menos un objetivo. Agrega uno antes de cambiar el tipo.',
              },
              VisitTarget: {
                Label: 'Tamaño objetivo de la visita',
                Help: 'Preguntas que un promotor puede completar en una visita de esta duración',
                NotSet: 'Sin definir',
                Value: '{{tasks}} tareas',
              },
              Stores: {
                Title: 'Tiendas · {{stores}} tiendas',
                ViewMore: 'Ver más',
                Count: '{{stores}} tiendas',
                ModalTitle: 'Tiendas · {{retailer}}',
                ModalSubtitle: 'Tiendas de este cronograma.',
                SearchPlaceholder: 'Buscar tiendas por nombre…',
                Empty: 'Ninguna tienda coincide con esta búsqueda.',
                LoadError: 'No pudimos cargar las tiendas de este cronograma.',
                Inactive: 'Inactiva',
                Footer: '{{shown}} de {{total}} tiendas',
                Done: 'Listo',
              },
            },
            Detail: {
              Loading: 'Un momento, estamos reuniendo este cronograma...',
              BackToList: 'Cronogramas',
              Configuration: 'Configuración',
              Eyebrow: 'Objetivos de merchandising',
              Send: 'Enviar',
              SendComingSoon: 'El envío estará disponible próximamente.',
              NoTasks: 'Todavía no hay tareas en este cronograma.',
              DraftFromLastWeek: 'Borrador de la semana anterior',
              CopyScheduleList: 'Copiar la lista del cronograma',
              CustomizeSuccess: 'Lista de la semana creada',
              CustomizeError: 'No se pudo crear la lista de la semana',
              LockedBadge: 'Publicada · bloqueada',
              StartedBadge: 'Semana en curso · solo lectura',
              EndedBadge: 'Cronograma terminado · solo lectura',
              Rename: {
                Edit: 'Renombrar cronograma',
                Input: 'Nombre del cronograma',
                Save: 'Guardar nombre',
                Cancel: 'Cancelar el cambio de nombre',
                Error: 'No se pudo renombrar el cronograma. Intenta de nuevo.',
              },
              Metadata: {
                Cadence: 'Frecuencia',
                Retailer: 'Cadena',
                Stores: 'Tiendas',
                FieldServices: 'Servicios de Campo',
                FirmWithStoreCount: '{{name}} ({{storeCount}})',
                None: '—',
              },
              Filters: {
                Area: 'Área',
                AllAreas: 'Todas las áreas',
              },
              Task: {
                Priority: 'Prioridad',
                Area: 'Área',
                Category: 'Categoría',
                NoCategory: '—',
                TaskType: 'Tipo de tarea',
                ReviewStatus: 'Estado de revisión',
                ReviewStatusAriaLabel: 'Estado de revisión para {{taskType}}',
                MayNotFit: 'Puede no entrar',
                NotContracted: 'No contratado',
                NotContractedHint:
                  'No todos los servicios de campo de este cronograma aprueban este tipo de tarea, así que no ocupa lugar en la visita ni cuenta como pendiente.',
                RemoveAriaLabel: 'Quitar {{taskType}}',
              },
              ReviewStatus: {
                pending: 'Pendiente',
                approved: 'Aprobado',
                rejected: 'Rechazado',
              },
              ReviewStatusPublished: 'Publicado',
              ReviewStatusUpdateError: 'No se pudo actualizar el estado de revisión. Intenta de nuevo.',
              PriorityUpdateError: 'No se pudo actualizar la prioridad. Intenta de nuevo.',
              RemoveTaskError: 'No se pudo quitar el objetivo. Intenta de nuevo.',
              RemoveTaskConfirm: '¿Quitar el objetivo {{taskType}}?',
              RemoveTaskConfirmWithCategory: '¿Quitar el objetivo {{taskType}} · {{category}}?',
              ListRefreshError:
                'No se pudieron recargar los objetivos. Recarga la página para ver los últimos cambios.',
              ApprovePendingButton_one: 'Aprobar {{count}} pendiente',
              ApprovePendingButton_other: 'Aprobar {{count}} pendientes',
              ApprovePendingConfirm_one: '¿Aprobar {{count}} tarea pendiente?',
              ApprovePendingConfirm_other: '¿Aprobar {{count}} tareas pendientes?',
              ApprovePendingSuccess_one: '{{count}} tarea aprobada',
              ApprovePendingSuccess_other: '{{count}} tareas aprobadas',
              ApprovePendingError: 'No se pudieron aprobar las tareas pendientes. Intenta de nuevo.',
              AddTask: {
                Button: 'Agregar objetivo',
                Title: 'Agregar objetivo',
                TaskType: 'Tipo de tarea',
                TaskTypePlaceholder: 'Selecciona un tipo de tarea…',
                Categories: 'Categorías',
                CategoriesPlaceholder: 'Selecciona categorías…',
                CategoriesLoading: 'Cargando categorías…',
                CategoriesError: 'No se pudieron cargar las categorías. Intenta de nuevo.',
                CategoriesHint: 'Los productos de las categorías seleccionadas serán auditados por esta tarea.',
                CategoriesSelectTypeFirst: 'Selecciona primero un tipo de tarea',
                CategoriesOptional: 'Opcional para este tipo de tarea.',
                NoPlannableTaskTypes:
                  'Ningún tipo de tarea está aprobado por todos los servicios de campo de este cronograma.',
                Area: 'Área',
                AreaPlaceholder: 'ej. Abarrotes, Lácteos…',
                Submit: 'Agregar objetivo',
                Success: 'Tarea agregada',
                Error: 'No se pudo agregar la tarea. Intenta de nuevo.',
              },
              Sidebar: {
                Collapse: 'Colapsar panel',
                Expand: 'Expandir panel',
                Resize: 'Redimensionar panel',
                ScheduleLabel: 'Cronograma',
                ObjectiveTitle: 'Objetivo',
                Composition: 'Composición',
                AlertCeiling: 'Techo de alertas',
                CompositionValue: {
                  scheduled_only: 'Solo programado',
                  alerts_only: 'Solo alertas',
                  hybrid: 'Alertas + programado',
                },
                AlertCeilingDoesNotApply: 'No aplica',
                AlertCeilingPercent: '{{value}}% de la visita',
                Period: {
                  WeekOf: 'Sem. del {{date}}',
                  PositionApproved: 'Semana {{current}} de {{total}} · aprobada',
                  PositionStarted: 'Semana {{current}} de {{total}} · en curso',
                  PositionPending_one: 'Semana {{current}} de {{total}} · {{count}} pendiente',
                  PositionPending_other: 'Semana {{current}} de {{total}} · {{count}} pendientes',
                  NoWeeks: 'No hay semanas de visita próximas',
                  LoadError: 'No se pudieron cargar las semanas de visita.',
                  PreviousAriaLabel: 'Período anterior',
                  NextAriaLabel: 'Período siguiente',
                },
                Utilization: {
                  Percent: '{{value}}%',
                  Cap: 'Techo',
                  Reset: 'Restablecer',
                  CustomizedThisWeek: 'Personalizado esta semana',
                  SaveError: 'No se pudo guardar el techo de alertas',
                  TickSlots: '{{capSlots}} de ~{{typicalSlots}} espacios',
                  CappedFirstLegend_one: '{{count}} alerta limitada primero',
                  CappedFirstLegend_other: '{{count}} alertas limitadas primero',
                  ScheduledLegend_one: '{{count}} objetivo de mercadeo',
                  ScheduledLegend_other: '{{count}} objetivos de mercadeo',
                  InfoTitle: 'Cómo funciona el techo',
                  MayNotFitCount_one: '{{count}} puede no entrar',
                  MayNotFitCount_other: '{{count}} pueden no entrar',
                  InfoNote:
                    'Las alertas se calculan y se limitan primero. Estos objetivos llenan el espacio que sobra.',
                  InfoContractNote:
                    'El tamaño de visita promedia {{typicalSlots}} tareas y varía según la duración, así que los espacios son aproximados. Cada semana puede modificar el techo por defecto del cronograma.',
                },
              },
            },
          },
        },
      },
    },
  });

export default i18n;
